import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLazyQuery, useMutation, useQuery, useSubscription } from '@apollo/client'
import { ArrowUUpLeft, ArrowUUpRight, Cards, CaretDown, CaretLeft, CaretRight, Crop, DotsSixVertical, FloppyDisk, Funnel, Gif, HandTap, Highlighter, Image as Images, ListBullets, MagnifyingGlass, PaintBrushBroad, Paragraph, PencilSimple, Plus, QrCode, Shapes, Spinner, Stop, TextAlignCenter, TextAlignJustify, TextAlignLeft, TextAlignRight, TextB, TextItalic, TextStrikethrough, TextT, TextUnderline, Trash, Upload, UploadSimple, X } from '@phosphor-icons/react'
import { RxCross1, RxLayers, RxLineHeight } from 'react-icons/rx'
import { RiDragMoveLine, RiImageAddLine, RiSearch2Line } from 'react-icons/ri'
import { Link, useParams } from 'react-router-dom'
import { TbBorderStyle } from 'react-icons/tb'
import * as htmlToImage from 'html-to-image';
import QRCode from "react-qr-code";
import { nanoid } from 'nanoid'
import moment from 'moment'

import "./style.css"

import Select from '../editor/options/select'
import ColorPick from '../editor/options/colorPick'
import FontPick from '../editor/options/fontPick'
import FontSize from '../editor/options/fontSize'
import Layer from '../editor/options/layer'
import Position from '../editor/options/position'
import Border from '../editor/options/border'
import Interactions from '../editor/elements/interactions'
import Paragraphs from '../editor/options/paragraph'

import TextComponent from '../editor/components/textComponent'
import ImageComponent from '../editor/components/imageComponent'
import SvgComponent from '../editor/components/svgComponent'
import MCQComponent from '../editor/components/mcqComponent'
import QRComponent from '../editor/components/qrComponent'

import text1 from '../assets/texts/1.png'
import { allFonts } from '../utils/allFonts'
import { GET_GIFS, GET_IMAGES, GET_ONE_PRESENTATION, GET_PRESENTATION_STATUS, GET_PRESENTATION_TITLE } from '../graphql/query'
import { heroIcons } from '../utils/icons'
import { CREATE_SLIDE, DELETE_PARTICIPANT, DELETE_SLIDE, UPDATE_PRESENTATION, UPDATE_PRESENTATION_STATUS } from '../graphql/mutation'
import toast from 'react-hot-toast'
import { ANSWER_CREATED, PARTICIPANT_JOINED } from '../graphql/subscription'
import useStore from '../utils/useStore'
import WordComponent from '../editor/components/wordComponent'
import useNetworkStatus from '../utils/network'
import QuestComponent from '../editor/components/questComponent'

export default function Presentation() {
    const params = useParams()

    const scale = useStore((state) => state.scale)
    const clickedPosition = useStore((state) => state.clickedPosition)
    const setClickedPosition = useStore((state) => state.setClickedPosition)
    const dragging = useStore((state) => state.dragging)
    const setDragging = useStore((state) => state.setDragging)
    const setResizing = useStore((state) => state.setResizing)
    const editing = useStore((state) => state.editing)
    const setEditing = useStore((state) => state.setEditing)
    const resizing = useStore((state) => state.resizing)
    const editElements = useStore((state) => state.editElement)
    const leftSelect = useStore((state) => state.leftSelect)
    const setLeftSelect = useStore((state) => state.setLeftSelect)
    const elements = useStore((state) => state.slides[state.select].elements)
    const addElements = useStore((state) => state.addElements)
    const removeAllSelects = useStore((state) => state.removeAllSelects)
    const selected = useStore((state) => state?.slides[state.select].elements?.find((e) => e?.id === state.selects[0]))
    const deleteElements = useStore((state) => state.deleteElements)
    const setGuide = useStore((state) => state.setGuide)
    const selects = useStore((state) => state?.selects)
    const fonts = useStore((state) => state?.fonts)
    const setFonts = useStore((state) => state?.setFonts)
    const files = useStore((state) => state?.files)
    const setTitle = useStore((state) => state.setTitle)
    const setSlides = useStore((state) => state.setSlides)

    const onMouseMove = (event) => {
        if (clickedPosition?.x === -1 || clickedPosition?.y === -1) {
            return
        } else if (editing) {
            return
        } else if (resizing && selected?.type === 'Text') {
            const inverseScale = 1 / scale;
            const deltaX = (event.clientX - clickedPosition?.x) * inverseScale
            const newWidth = selected?.preWidth + deltaX
            if (newWidth > 10) {
                const style = { ...selected?.style, width: newWidth }
                editElements("style", style, selected?.id)
            }
        } else if (resizing && selected?.type === 'Image') {
            const inverseScale = 1 / scale;
            const deltaY = (event.clientY - clickedPosition?.y) * inverseScale
            const deltaX = (event.clientX - clickedPosition?.x) * inverseScale
            const aspectRatio = selected?.preHeight / selected?.preWidth
            const newHeight = resizing?.side === 'top-left' || resizing?.side === 'top-right' ? selected?.preHeight - deltaY : selected?.preHeight + deltaY
            if (newHeight > 30) {
                const newWidth = event.shiftKey ? selected?.preWidth + deltaX : newHeight / aspectRatio;
                const style = { ...selected?.style, height: newHeight, width: newWidth }
                editElements("style", style, selected?.id)
                if (resizing?.side === 'top-left') {
                    editElements("currPosition", { x: selected?.prevPosition?.x + (selected?.preWidth - newWidth), y: selected?.prevPosition?.y + (selected?.preHeight - newHeight) }, selected?.id)
                } else if (resizing?.side === 'bottom-left') {
                    editElements("currPosition", { x: selected?.prevPosition?.x + (selected?.preWidth - newWidth), y: selected?.prevPosition?.y }, selected?.id)
                } else if (resizing?.side === 'top-right') {
                    editElements("currPosition", { x: selected?.prevPosition?.x, y: selected?.prevPosition?.y + (selected?.preHeight - newHeight) }, selected?.id)
                }
            }
        } else if (resizing && selected?.type === 'SVG') {
            const inverseScale = 1 / scale;
            const deltaX = (event.clientX - clickedPosition?.x) * inverseScale
            const deltaY = (event.clientY - clickedPosition?.y) * inverseScale
            const aspectRatio = selected?.preHeight / selected?.preWidth
            const newHeight = resizing?.side === 'top-left' || resizing?.side === 'top-right' ? selected?.preHeight - deltaY : selected?.preHeight + deltaY
            if (newHeight > 30) {
                const newWidth = event.shiftKey ? selected?.preWidth + deltaX : newHeight / aspectRatio;
                const style = { ...selected?.style, height: newHeight, width: newWidth }
                editElements("style", style, selected?.id)
                if (resizing?.side === 'top-left') {
                    editElements("currPosition", { x: selected?.prevPosition?.x + (selected?.preWidth - newWidth), y: selected?.prevPosition?.y + (selected?.preHeight - newHeight) }, selected?.id)
                } else if (resizing?.side === 'bottom-left') {
                    editElements("currPosition", { x: selected?.prevPosition?.x + (selected?.preWidth - newWidth), y: selected?.prevPosition?.y }, selected?.id)
                } else if (resizing?.side === 'top-right') {
                    editElements("currPosition", { x: selected?.prevPosition?.x, y: selected?.prevPosition?.y + (selected?.preHeight - newHeight) }, selected?.id)
                }
            }
        } else if (resizing && selected?.type === 'MCQ') {
            const inverseScale = 1 / scale;
            const deltaX = (event.clientX - clickedPosition?.x) * inverseScale
            const deltaY = (event.clientY - clickedPosition?.y) * inverseScale
            const aspectRatio = selected?.preHeight / selected?.preWidth
            const newHeight = resizing?.side === 'top-left' || resizing?.side === 'top-right' ? selected?.preHeight - deltaY : selected?.preHeight + deltaY
            if (newHeight > 30) {
                const newWidth = event.shiftKey ? selected?.preWidth + deltaX : newHeight / aspectRatio;
                const style = { ...selected?.style, height: newHeight, width: newWidth }
                editElements("style", style, selected?.id)
                if (resizing?.side === 'top-left') {
                    editElements("currPosition", { x: selected?.prevPosition?.x + (selected?.preWidth - newWidth), y: selected?.prevPosition?.y + (selected?.preHeight - newHeight) }, selected?.id)
                } else if (resizing?.side === 'bottom-left') {
                    editElements("currPosition", { x: selected?.prevPosition?.x + (selected?.preWidth - newWidth), y: selected?.prevPosition?.y }, selected?.id)
                } else if (resizing?.side === 'top-right') {
                    editElements("currPosition", { x: selected?.prevPosition?.x, y: selected?.prevPosition?.y + (selected?.preHeight - newHeight) }, selected?.id)
                }
            }
        } else if (resizing && selected?.type === 'Word Cloud') {
            const inverseScale = 1 / scale;
            const deltaX = (event.clientX - clickedPosition?.x) * inverseScale
            const deltaY = (event.clientY - clickedPosition?.y) * inverseScale
            const aspectRatio = selected?.preHeight / selected?.preWidth
            const newHeight = resizing?.side === 'top-left' || resizing?.side === 'top-right' ? selected?.preHeight - deltaY : selected?.preHeight + deltaY
            if (newHeight > 30) {
                const newWidth = event.shiftKey ? selected?.preWidth + deltaX : newHeight / aspectRatio;
                const style = { ...selected?.style, height: newHeight, width: newWidth }
                editElements("style", style, selected?.id)
                if (resizing?.side === 'top-left') {
                    editElements("currPosition", { x: selected?.prevPosition?.x + (selected?.preWidth - newWidth), y: selected?.prevPosition?.y + (selected?.preHeight - newHeight) }, selected?.id)
                } else if (resizing?.side === 'bottom-left') {
                    editElements("currPosition", { x: selected?.prevPosition?.x + (selected?.preWidth - newWidth), y: selected?.prevPosition?.y }, selected?.id)
                } else if (resizing?.side === 'top-right') {
                    editElements("currPosition", { x: selected?.prevPosition?.x, y: selected?.prevPosition?.y + (selected?.preHeight - newHeight) }, selected?.id)
                }
            }
        } else if (resizing && selected?.type === 'Q&A') {
            const inverseScale = 1 / scale;
            const deltaX = (event.clientX - clickedPosition?.x) * inverseScale
            const deltaY = (event.clientY - clickedPosition?.y) * inverseScale
            const aspectRatio = selected?.preHeight / selected?.preWidth
            const newHeight = resizing?.side === 'top-left' || resizing?.side === 'top-right' ? selected?.preHeight - deltaY : selected?.preHeight + deltaY
            if (newHeight > 30) {
                const newWidth = event.shiftKey ? selected?.preWidth + deltaX : newHeight / aspectRatio;
                const style = { ...selected?.style, height: newHeight, width: newWidth }
                editElements("style", style, selected?.id)
                if (resizing?.side === 'top-left') {
                    editElements("currPosition", { x: selected?.prevPosition?.x + (selected?.preWidth - newWidth), y: selected?.prevPosition?.y + (selected?.preHeight - newHeight) }, selected?.id)
                } else if (resizing?.side === 'bottom-left') {
                    editElements("currPosition", { x: selected?.prevPosition?.x + (selected?.preWidth - newWidth), y: selected?.prevPosition?.y }, selected?.id)
                } else if (resizing?.side === 'top-right') {
                    editElements("currPosition", { x: selected?.prevPosition?.x, y: selected?.prevPosition?.y + (selected?.preHeight - newHeight) }, selected?.id)
                }
            }
        } else if (resizing && selected?.type === 'QR') {
            const inverseScale = 1 / scale;
            const deltaX = (event.clientX - clickedPosition?.x) * inverseScale
            const deltaY = (event.clientY - clickedPosition?.y) * inverseScale
            const aspectRatio = selected?.preHeight / selected?.preWidth
            const newHeight = resizing?.side === 'top-left' || resizing?.side === 'top-right' ? selected?.preHeight - deltaY : selected?.preHeight + deltaY
            if (newHeight > 30) {
                const newWidth = event.shiftKey ? selected?.preWidth + deltaX : newHeight / aspectRatio;
                const style = { ...selected?.style, height: newHeight, width: newWidth }
                editElements("style", style, selected?.id)
                if (resizing?.side === 'top-left') {
                    editElements("currPosition", { x: selected?.prevPosition?.x + (selected?.preWidth - newWidth), y: selected?.prevPosition?.y + (selected?.preHeight - newHeight) }, selected?.id)
                } else if (resizing?.side === 'bottom-left') {
                    editElements("currPosition", { x: selected?.prevPosition?.x + (selected?.preWidth - newWidth), y: selected?.prevPosition?.y }, selected?.id)
                } else if (resizing?.side === 'top-right') {
                    editElements("currPosition", { x: selected?.prevPosition?.x, y: selected?.prevPosition?.y + (selected?.preHeight - newHeight) }, selected?.id)
                }
            }
        } else if (!resizing && !editing) {
            const snapDist = 5
            const width = 480
            const height = 854
            setDragging(true)
            const inverseScale = 1 / scale;
            const deltaX = (event.clientX - clickedPosition?.x) * inverseScale
            const deltaY = (event.clientY - clickedPosition?.y) * inverseScale
            // const snappedX = Math.round(deltaX / gridSize) * gridSize;
            // const snappedY = Math.round(deltaY / gridSize) * gridSize;
            // const snappedX = deltaX || Math.round((deltaX - selected?.prevPosition?.x) / gridSize) * gridSize + selected?.prevPosition?.x;
            // const snappedY = deltaY || Math.round((deltaY - selected?.prevPosition?.y) / gridSize) * gridSize + selected?.prevPosition?.y;
            if (selects?.length > 1) {
                elements?.filter((e) => selects?.includes(e?.id))?.forEach((itm) => {
                    editElements("currPosition", { x: itm?.prevPosition?.x + deltaX, y: itm?.prevPosition?.y + deltaY }, itm?.id)
                    setGuide(null)
                })
            } else {
                if ((selected?.preWidth / 2) + (selected?.prevPosition?.x + deltaX) > ((height / 2) - snapDist) && (selected?.preWidth / 2) + (selected?.prevPosition?.x + deltaX) < ((height / 2) + snapDist) && (selected?.preHeight / 2) + (selected?.prevPosition?.y + deltaY) > ((width / 2) - snapDist) && (selected?.preHeight / 2) + (selected?.prevPosition?.y + deltaY) < ((width / 2) + snapDist)) {
                    editElements("currPosition", { x: (height / 2) - (selected?.preWidth / 2), y: (width / 2) - (selected?.preHeight / 2) }, selected?.id)
                    setGuide("Center")
                } else if ((selected?.preWidth / 2) + (selected?.prevPosition?.x + deltaX) > ((height / 2) - snapDist) && (selected?.preWidth / 2) + (selected?.prevPosition?.x + deltaX) < ((height / 2) + snapDist) && selected?.prevPosition?.y + deltaY < 10 && selected?.prevPosition?.y + deltaY > -10) {
                    editElements("currPosition", { x: (height / 2) - (selected?.preWidth / 2), y: 0 }, selected?.id)
                    setGuide("H-Top")
                } else if ((selected?.preWidth / 2) + (selected?.prevPosition?.x + deltaX) > ((height / 2) - snapDist) && (selected?.preWidth / 2) + (selected?.prevPosition?.x + deltaX) < ((height / 2) + snapDist) && selected?.preHeight + (selected?.prevPosition?.y + deltaY) > (width - snapDist) && selected?.preHeight + (selected?.prevPosition?.y + deltaY) < (width + snapDist)) {
                    editElements("currPosition", { x: (height / 2) - (selected?.preWidth / 2), y: width - selected.preHeight }, selected?.id)
                    setGuide("H-Bottom")
                } else if ((selected?.preWidth / 2) + (selected?.prevPosition?.x + deltaX) > ((height / 2) - snapDist) && (selected?.preWidth / 2) + (selected?.prevPosition?.x + deltaX) < ((height / 2) + snapDist)) {
                    editElements("currPosition", { x: (height / 2) - (selected?.preWidth / 2), y: selected?.prevPosition?.y + deltaY }, selected?.id)
                    setGuide("H-Center")
                } else if ((selected?.preHeight / 2) + (selected?.prevPosition?.y + deltaY) > ((width / 2) - snapDist) && (selected?.preHeight / 2) + (selected?.prevPosition?.y + deltaY) < ((width / 2) + snapDist) && selected?.prevPosition?.x + deltaX < 10 && selected?.prevPosition?.x + deltaX > -10) {
                    editElements("currPosition", { x: 0, y: (width / 2) - (selected?.preHeight / 2) }, selected?.id)
                    setGuide("V-Left")
                } else if ((selected?.preHeight / 2) + (selected?.prevPosition?.y + deltaY) > ((width / 2) - snapDist) && (selected?.preHeight / 2) + (selected?.prevPosition?.y + deltaY) < ((width / 2) + snapDist) && selected?.preWidth + (selected?.prevPosition?.x + deltaX) > (height - snapDist) && selected?.preWidth + (selected?.prevPosition?.x + deltaX) < (height + snapDist)) {
                    editElements("currPosition", { x: height - selected?.preWidth, y: (width / 2) - (selected?.preHeight / 2) }, selected?.id)
                    setGuide("V-Right")
                } else if ((selected?.preHeight / 2) + (selected?.prevPosition?.y + deltaY) > ((width / 2) - snapDist) && (selected?.preHeight / 2) + (selected?.prevPosition?.y + deltaY) < ((width / 2) + snapDist)) {
                    editElements("currPosition", { x: selected?.prevPosition?.x + deltaX, y: (width / 2) - (selected?.preHeight / 2) }, selected?.id)
                    setGuide("V-Center")
                } else if (selected?.preWidth + (selected?.prevPosition?.x + deltaX) > (height - snapDist) && selected?.preWidth + (selected?.prevPosition?.x + deltaX) < (height + snapDist)) {
                    editElements("currPosition", { x: height - selected?.preWidth, y: selected?.prevPosition?.y + deltaY }, selected?.id)
                    setGuide("Right")
                } else if (selected?.preHeight + (selected?.prevPosition?.y + deltaY) > (width - snapDist) && selected?.preHeight + (selected?.prevPosition?.y + deltaY) < (width + snapDist)) {
                    editElements("currPosition", { x: selected?.prevPosition?.x + deltaX, y: width - selected.preHeight }, selected?.id)
                    setGuide("Bottom")
                } else if (selected?.prevPosition?.y + deltaY < 10 && selected?.prevPosition?.y + deltaY > -10 && selected?.prevPosition?.x + deltaX < 10 && selected?.prevPosition?.x + deltaX > -10) {
                    editElements("currPosition", { x: 0, y: 0 }, selected?.id)
                    setGuide("Top-Left")
                } else if (selected?.prevPosition?.x + deltaX < 10 && selected?.prevPosition?.x + deltaX > -10) {
                    editElements("currPosition", { x: 0, y: selected?.prevPosition?.y + deltaY }, selected?.id)
                    setGuide("Left")
                } else if (selected?.prevPosition?.y + deltaY < 10 && selected?.prevPosition?.y + deltaY > -10) {
                    editElements("currPosition", { x: selected?.prevPosition?.x + deltaX, y: 0 }, selected?.id)
                    setGuide("Top")
                } else {
                    editElements("currPosition", { x: selected?.prevPosition?.x + deltaX, y: selected?.prevPosition?.y + deltaY }, selected?.id)
                    setGuide(null)
                }
            }
        }
    }

    const onImageCreate = (url) => {
        const img = new Image();
        img.src = url
        img.onload = () => {
            const aspectRatio = img.width / img.height
            const height = img.height > 200 ? 200 : img.height
            const width = img.height > 200 ? 200 * aspectRatio : img.width
            addElements({ id: nanoid(7), type: "Image", value: url, style: { height: height, width: width }, preWidth: width, preHeight: height, currPosition: { x: 10, y: 10 }, prevPosition: { x: 10, y: 10 } })
        }
    }

    const onSvgCreate = async (val, url) => {
        if (url) {
            const response = await fetch(val);
            const svgText = await response.text();
            const hexColorRegex = /#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})/g;
            const colors = svgText.match(hexColorRegex)
            const uniqueColors = [...new Set(colors)];
            addElements({
                id: nanoid(7), type: "SVG", value: svgText, style: { height: 100, width: 100 }, preWidth: 100, preHeight: 100, currPosition: { x: 10, y: 10 }, prevPosition: { x: 10, y: 10 }, colors: uniqueColors?.map((itm) => {
                    return {
                        [itm]: itm
                    }
                })
            })
        } else {
            const hexColorRegex = /#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})/g;
            const colors = val.match(hexColorRegex)
            const uniqueColors = [...new Set(colors)];
            addElements({
                id: nanoid(7), type: "SVG", value: val, style: { height: 100, width: 100 }, preWidth: 100, preHeight: 100, currPosition: { x: 10, y: 10 }, prevPosition: { x: 10, y: 10 }, colors: uniqueColors?.map((itm) => {
                    return {
                        [itm]: itm
                    }
                })
            })
        }
    }

    const onPaste = (event) => {
        console.log("Paste");
        const items = event?.clipboardData?.items
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.type.startsWith('image/')) {
                setLeftSelect("Upload")
                const blob = item.getAsFile();
                const url = URL.createObjectURL(blob);
                onImageCreate(url)
                break;
            }
        }
    }

    useEffect(() => {
        document.addEventListener('paste', onPaste)
        return () => {
            document.removeEventListener('paste', onPaste)
        }
    }, [])

    const onGroup = () => {
        const allSelected = elements?.filter((e) => selects?.includes(e?.id))
        let left = false
        let top = false
        let height = false
        let width = false

        allSelected?.forEach((itm) => {
            const newHeight = itm?.style?.height || itm?.preHeight
            const newWidth = itm?.style?.width || itm?.preWidth
            if (left === false) {
                left = itm?.currPosition?.x
            } else if (itm?.currPosition?.x < left) {
                left = itm?.currPosition?.x
            }
            if (top === false) {
                top = itm?.currPosition?.y
            } else if (itm?.currPosition?.y < top) {
                top = itm?.currPosition?.y
            }
            if (height === false) {
                height = itm?.currPosition?.y + newHeight
            } else if (itm?.currPosition?.y + newHeight > height) {
                height = itm?.currPosition?.y + newHeight
            }
            if (width === false) {
                width = itm?.currPosition?.x + newWidth
            } else if (itm?.currPosition?.x + newWidth > width) {
                width = itm?.currPosition?.x + newWidth
            }
        })
        const group = {
            id: nanoid(7), type: "Group", style: { width: width - left, height: height - top }, elements: allSelected?.map((itm) => {
                return {
                    ...itm,
                    prevPosition: { x: itm?.currPosition?.x - left, y: itm?.currPosition?.y - top },
                    currPosition: { x: itm?.currPosition?.x - left, y: itm?.currPosition?.y - top }
                }
            }), currPosition: { x: left, y: top }
        }
        deleteElements()
        addElements(group)
    }

    const { data, loading } = useQuery(GET_ONE_PRESENTATION, {
        variables: {
            getPresentationId: params?.id
        },
        fetchPolicy: "network-only",
        onCompleted: (datas) => {
            document.title = `XAND | ${datas?.getPresentation?.title}`
            setTitle(datas?.getPresentation?.title)
            setSlides(datas?.getPresentation?.allSlides?.map((itm) => {
                return {
                    ...itm,
                    elements: itm?.elements ? JSON.parse(itm?.elements) : []
                }
            }))
        }
    })

    if (loading) {
        return (
            <div className='Loading'>
                <div className='Loader' />
            </div>
        )
    }
    return (
        <div className='Presentation'>
            <Header data={data?.getPresentation} />
            <Network />
            <FontLoader />
            <KeyboardShortcut />
            <div className='Presentation_Con'>
                <SideBar inter={selected?.type === 'MCQ' || selected?.type === 'QR' || selected?.type === 'Word Cloud' || selected?.type === 'Q&A'} />
                <div className='Presentation_Left'>
                    {leftSelect === 'Slides' ?
                        <>
                            <NewSlideBtn />
                            <Slides />
                        </>
                        : leftSelect === 'Texts' ?
                            <>
                                <div className='h-5' />
                                <div className='Presentation_Left_Text_Con'>
                                    {texts?.map((item, index) => {
                                        return (
                                            <div key={index} style={item?.style} className='Presentation_Left_Text' onClick={() => addElements({ id: nanoid(7), type: "Text", value: "Lorem Ipsum", style: { ...item?.style, width: 200 }, preWidth: 200, currPosition: { x: 10, y: 10 }, prevPosition: { x: 10, y: 10 } })}>
                                                {item?.text}
                                            </div>
                                        )
                                    })}
                                </div>
                                <div className='Presentation_Left_Text_Con'>
                                    {styledText?.map((item, index) => {
                                        if (item?.image) {
                                            return (
                                                <div key={index} className='Presentation_Left_Text' onClick={() => {
                                                    addElements({
                                                        ...item?.element, id: nanoid(7), prevPosition: { x: 0, y: 0 }, currPosition: { x: 0, y: 0 }, elements: item?.element?.elements?.map((itm) => {
                                                            return {
                                                                id: nanoid(7),
                                                                ...itm
                                                            }
                                                        })
                                                    })
                                                    setFonts([...fonts, item?.element?.elements?.map((itm) => {
                                                        return { font: itm?.style?.fontFamily, count: 1 }
                                                    })])
                                                }}>
                                                    <img onDragStart={(e) => e?.preventDefault()} src={item?.image} />
                                                </div>
                                            )
                                        }
                                        return (
                                            <div key={index} style={item?.style} className='Presentation_Left_Text' onClick={() => addElements({ id: nanoid(7), type: "Text", value: "Lorem Ipsum", style: { ...item?.style, width: 200 }, preWidth: 200, currPosition: { x: 10, y: 10 }, prevPosition: { x: 10, y: 10 } })}>
                                                {item?.text}
                                            </div>
                                        )
                                    })}
                                </div>
                                {/* <div className='Presentation_Left_Text_Con'>
                                    {groupText?.map((item, index)=>{
                                        return (
                                            <div key={index} className='Presentation_Left_Text' onClick={() => addElements({...item?.element, id: nanoid(7), prevPosition:{x:0, y:0}, currPosition:{x:0,y:0}, elements: item?.element?.elements?.map((itm)=>{
                                                return {
                                                    id: nanoid(7),
                                                    ...itm
                                                }
                                            })})}>
                                                <img src={item?.image}/>
                                            </div>
                                        )
                                    })}
                                </div> */}
                            </>
                            : leftSelect === 'Images' ?
                                <AllImages onImageCreate={onImageCreate} />
                                : leftSelect === "Gif" ?
                                    <AllGifs onImageCreate={onImageCreate} />
                                    : leftSelect === 'Shapes' ?
                                        <>
                                            <div className='h-5' />
                                            <div className='Presentation_Left_Image_Search'>
                                                <RiSearch2Line className='Presentation_Left_Image_Search_Icon' />
                                                <input placeholder='Search Shapes' onKeyDown={(e) => e?.stopPropagation()} />
                                            </div>
                                            <div className='Presentation_Left_Svg_Con'>
                                                <div className='Presentation_Left_Svg_Con1'>
                                                    {heroIcons?.map((item, index) => {
                                                        if (index % 3 === 0) {
                                                            return <div key={index} className='Presentation_Left_Image' dangerouslySetInnerHTML={{ __html: item?.svg }} onClick={() => onSvgCreate(item?.svg)} />
                                                        } else {
                                                            return null
                                                        }
                                                    })}
                                                </div>
                                                <div className='Presentation_Left_Svg_Con1'>
                                                    {heroIcons?.map((item, index) => {
                                                        if (index % 3 === 1) {
                                                            return <div key={index} className='Presentation_Left_Image' dangerouslySetInnerHTML={{ __html: item?.svg }} onClick={() => onSvgCreate(item?.svg)} />
                                                        } else {
                                                            return null
                                                        }
                                                    })}
                                                </div>
                                                <div className='Presentation_Left_Svg_Con1'>
                                                    {heroIcons?.map((item, index) => {
                                                        if (index % 3 === 2) {
                                                            return <div key={index} className='Presentation_Left_Image' dangerouslySetInnerHTML={{ __html: item?.svg }} onClick={() => onSvgCreate(item?.svg)} />
                                                        } else {
                                                            return null
                                                        }
                                                    })}
                                                </div>
                                            </div>
                                        </>
                                        : leftSelect === 'Interactions' ?
                                            <Interactions useStore={useStore} />
                                            : leftSelect === 'Edit' ?
                                                selected?.type === 'MCQ' ?
                                                    <MCQEdit />
                                                    : selected?.type === 'Word Cloud' ?
                                                        <WordEdit />
                                                        : selected?.type === 'Q&A' ?
                                                            <QAEdit />
                                                            :
                                                            <QREdit />
                                                : leftSelect === 'Upload' ?
                                                    <>
                                                        <UploadBtn />
                                                        <div className='Presentation_Left_Image_Con'>
                                                            {files?.length === 0 ?
                                                                <div className='Presentation_Left_Image_Empty'>
                                                                    <img onDragStart={(e) => e?.preventDefault()} style={{ filter: "grayscale(100%)" }} width="150" height="150" src="https://img.icons8.com/cute-clipart/400/nothing-found.png" alt="nothing-found" />
                                                                    No files to show here
                                                                </div>
                                                                :
                                                                <>
                                                                    <div className='Presentation_Left_Image_Con1'>
                                                                        {files?.map((item, index) => {
                                                                            if (index % 2 === 0) {
                                                                                return <img key={index} alt='' className='Presentation_Left_Image' src={item?.url} onClick={() => item?.type === 'image/svg+xml' ? onSvgCreate(item?.url, true) : onImageCreate(item?.url)} />
                                                                            } else {
                                                                                return null
                                                                            }
                                                                        })}
                                                                    </div>
                                                                    <div className='Presentation_Left_Image_Con1'>
                                                                        {files?.map((item, index) => {
                                                                            if (index % 2 !== 0) {
                                                                                return <img key={index} alt='' className='Presentation_Left_Image' src={item?.url} onClick={() => item?.type === 'image/svg+xml' ? onSvgCreate(item?.url, true) : onImageCreate(item?.url)} />
                                                                            } else {
                                                                                return null
                                                                            }
                                                                        })}
                                                                    </div>
                                                                </>}
                                                        </div>
                                                    </>
                                                    :
                                                    null}
                    <div className='h-15' />
                </div>
                <div className='Presentation_Middle'>
                    <div className='Presentation_Middle_Top'>
                        {selected?.type === 'Text' ?
                            <TextOptions />
                            : selected?.type === 'Image' ?
                                <ImageOptions />
                                : selected?.type === 'SVG' ?
                                    <SvgOptions />
                                    : selected?.type === 'MCQ' || selected?.type === 'Word Cloud' || selected?.type === 'Q&A' ?
                                        <MCQOptions />
                                        : selected?.type === 'Group' ?
                                            <GroupOption />
                                            :
                                            <CanvasOptions />
                        }
                        {selected?.type === 'MCQ' ? null : selected?.type !== 'Group' && selects?.length > 1 ? <div onClick={() => onGroup()} className='Presentation_Middle_Top_Btn1'>Group</div> : null}
                        <div className='FlexCon' />
                        <div className='Presentation_Middle_Top_Sep_Line' />
                        <DeleteOptions />
                        <LayerOptions />
                        <div className='Presentation_Middle_Top_Sep' />
                    </div>
                    <div onContextMenu={(e) => e.preventDefault()} style={{ cursor: resizing ? resizing?.cursor : dragging ? "move" : "default" }} className={'Presentation_Middle_Middle'} onMouseUp={() => {
                        setClickedPosition({ x: -1, y: -1 })
                        setGuide(null)
                        setTimeout(() => {
                            setDragging(false)
                            setResizing(false)
                        }, 100);
                    }} onMouseMove={onMouseMove} onClick={() => {
                        window.getSelection().removeAllRanges()
                        if (!resizing) {
                            if (leftSelect === 'Edit') {
                                removeAllSelects()
                                setLeftSelect("Slides")
                            } else {
                                removeAllSelects()
                            }
                        }
                        setEditing(false)
                        setResizing(false)
                    }}>
                        <Slide elements={elements} data={data?.getPresentation} />
                    </div>
                    <textarea placeholder='Add presentation notes' />
                </div>
            </div>
            <ImageModal />
            <SlideModal elements={elements} data={data?.getPresentation} />
        </div>
    )
}

const SlideModal = React.memo(({ data, elements }) => {
    const params = useParams()

    const outerDivRef = useRef(null);
    const [qr, setQr] = useState(false)
    const [ans, setAns] = useState(false)
    const select = useStore((state) => state.select)
    const scaleFull = useStore((state) => state.scaleFull)
    const setScaleFull = useStore((state) => state.setScaleFull)
    const isFullScreen = useStore((state) => state.isFullScreen)
    const background = useStore((state) => state.slides[state.select].background)
    const bgType = useStore((state) => state.slides[state.select].bgType)
    const setSelectPrev = useStore((state) => state.setSelectPrev)
    const setSelectNext = useStore((state) => state.setSelectNext)

    const handleResize = () => {
        if (outerDivRef.current) {
            const { offsetWidth, offsetHeight } = outerDivRef.current;
            const scaleFactor = Math.min(offsetWidth / 854, offsetHeight / 480);
            setScaleFull(scaleFactor)
        }
    };

    useSubscription(PARTICIPANT_JOINED, {
        variables: {
            "code": data?.code
        },
        onData: (datas) => {
            datas.client.writeQuery({
                query: GET_ONE_PRESENTATION,
                variables: {
                    getPresentationId: params?.id
                },
                data: {
                    getPresentation: {
                        ...data,
                        participants: data?.participants?.length > 0 ? [
                            ...data?.participants,
                            datas.data.data?.participantJoined
                        ] : [datas.data.data?.participantJoined]
                    }
                }
            })
        }
    })

    useSubscription(ANSWER_CREATED, {
        variables: {
            "code": data?.code
        },
        onData: (datas) => {
            datas.client.writeQuery({
                query: GET_ONE_PRESENTATION,
                variables: {
                    getPresentationId: params?.id
                },
                data: {
                    getPresentation: {
                        ...data,
                        allSlides: data?.allSlides?.map((itm) => {
                            if (itm?._id === datas?.data?.data?.answerCreated?.slideId) {
                                return {
                                    ...itm,
                                    answers: [
                                        ...itm?.answers,
                                        { __typename: "Answer", _id: datas?.data?.data?.answerCreated?._id, answer: datas?.data?.data?.answerCreated?.answer, createdAt: datas?.data?.data?.answerCreated?.createdAt, participantId: datas?.data?.data?.answerCreated?.participantId }
                                    ]
                                }
                            }
                            return itm
                        })
                    }
                }
            })
        }
    })

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        handleResize()
    }, [isFullScreen])

    const answers = data?.allSlides?.[select]?.answers

    const currentUrl = window.location.origin

    return (
        <div ref={outerDivRef} className={isFullScreen ? 'Modal Modal_Show1' : 'Modal'}>
            {isFullScreen ?
                <>{qr ?
                    <div style={{ transform: `scale(${scaleFull})` }} className='Modal_Info_Con'>
                        <div className='Modal_Info'>
                            <div className='Modal_Info_Left'>
                                <QRCode value={`${currentUrl}/join/${data?.code}`} className='Modal_Info_Left_QR' />
                                <div className='Modal_Info_Left_Text'>Go to {currentUrl} and enter code <b>{data?.code}</b></div>
                            </div>
                            <div className='Modal_Info_Right'>
                                <div className='Modal_Info_Right_Title'>Joined Users <b>{data?.participants?.length}</b></div>
                                <div className='Modal_Info_Right_Body'>
                                    {data?.participants?.map((item, index) => {
                                        return (
                                            <div key={index} className='Modal_Info_Right_User'>
                                                {index + 1}. {item?.name}
                                                <div className='FlexCon' />
                                                <DeleteParticipant item={item} />
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                    :
                    <div style={{ transform: `scale(${scaleFull})`, backgroundColor: bgType === 'Solid Color' ? background : null, backgroundImage: bgType !== 'Solid Color' ? background : "none", border: "none", overflow: "hidden" }} className='Presentation_Middle_Canvas'>
                        {elements?.map((item, index) => {
                            if (item?.type === 'Text') {
                                return <TextComponent key={index} item={item} isFullScreen={isFullScreen} />
                            } else if (item?.type === 'Image') {
                                return <ImageComponent key={index} item={item} isFullScreen={isFullScreen} />
                            } else if (item?.type === 'SVG') {
                                return <SvgComponent key={index} item={item} isFullScreen={isFullScreen} />
                            } else if (item?.type === 'MCQ') {
                                return <MCQComponent key={index} item={item} isFullScreen={isFullScreen} answers={answers} />
                            } else if (item?.type === 'QR') {
                                return <QRComponent key={index} item={item} isFullScreen={isFullScreen} />
                            } else if (item?.type === 'Group') {
                                return <GroupComponent key={index} item={item} isFullScreen={isFullScreen} />
                            } else if (item?.type === 'Word Cloud') {
                                return <WordComponent key={index} item={item} isFullScreen={isFullScreen} answers={answers} isLive={data?.status === "Live"} />
                            } else if (item?.type === 'Q&A') {
                                return <QuestComponent key={index} item={item} isFullScreen={isFullScreen} answers={answers} />
                            } else {
                                return null
                            }
                        })}
                        <div className='Presentation_Middle_Code'>
                            Go to <b>{currentUrl}</b> and enter code <b>{data?.code}</b>
                        </div>
                    </div>
                }
                </>
                : null}
            <div style={{ transform: `scale(${scaleFull})` }} className={ans ? 'Modal_Ans_Con Modal_Ans_Con_Show' : 'Modal_Ans_Con'}>
                <div className='Modal_Info_Right_Title'>Answers <b>{answers?.length}</b></div>
                {answers?.map((item, index) => {
                    return (
                        <div key={index} className='Modal_Ans'>
                            <div className='Modal_Ans_Name'>{data?.participants?.find((e) => e?.uuid === item?.participantId)?.name}: {item?.answer}</div>
                            <div className='Modal_Ans_Date'>{moment(item?.createdAt * 1)?.format('LTS')}</div>
                        </div>
                    )
                })}
            </div>
            <div className='Modal_Option_Con'>
                <div className='Modal_Option' onClick={() => setQr(!qr)}>
                    <QrCode />
                </div>
                <div className='Modal_Option1' onClick={() => setAns(!ans)}>
                    Answers
                </div>
                <div className='Modal_Option' onClick={() => setSelectPrev()}>
                    <CaretLeft />
                </div>
                <div className='Modal_Option_Text'>
                    {select + 1}
                </div>
                <div className='Modal_Option' onClick={() => setSelectNext()}>
                    <CaretRight />
                </div>
            </div>
        </div>
    )
})

const DeleteParticipant = React.memo(({item}) => {
    const [deletes, deleting] = useMutation(DELETE_PARTICIPANT)
    return (
        deleting?.loading ?
            <Spinner className='Presentation_Preview_Add_Load' size={17} />
            :
            <Trash size={17} color='red' style={{ cursor: "pointer" }} onClick={() => deletes({
                variables: {
                    "deleteParticipantId": item?.uuid
                }
            })} />
    )
})

const ImageModal = React.memo(() => {
    const show = useStore((state) => state.showModal)
    const setShow = useStore((state) => state.setShowModal)
    const files = useStore((state) => state?.files)
    const setFiles = useStore((state) => state?.setFiles)
    const selectFiles = useStore((state) => state?.selectFiles)
    const setSelectFiles = useStore((state) => state?.setSelectFiles)

    const onUpload = () => {
        selectFiles?.forEach((itm) => {
            setFiles(itm)
        })
        setShow(false)
    }

    const onPick = (allFiles) => {
        const files = Array.from(allFiles);
        files.forEach((file) => {
            setSelectFiles({ url: URL.createObjectURL(file), type: file?.type })
        });
    }

    const onURL = (url) => {
        const img = new Image()
        img.src = url
        img.onload = () => {
            if (!files?.find((e) => e?.url === url)) {
                setSelectFiles({ url: url, type: "" })
                setTimeout(() => {
                    document.getElementById("Image_URL_Input").value = ""
                }, 1000);
            }
        }
        img.onerror = () => {
            alert("Invalid Image URL")
            document.getElementById("Image_URL_Input").value = ""
        }
    }

    const onDrop = (event) => {
        event.preventDefault()
        onPick([...event?.dataTransfer.files])
    }

    const handleDragEnter = (event) => {
        event.preventDefault();
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
    };

    return (
        <div className={show ? 'Modal Modal_Show' : 'Modal'}>
            <div className='Modal_Upload_Con'>
                <h2>Upload File</h2>
                <label onDrop={onDrop} onDragLeave={handleDragLeave} onDragEnter={handleDragEnter} onDragOver={handleDragEnter} className={'Modal_File_Upload_Con'}>
                    <Upload size={40} weight='fill' />
                    <h3><b>Click to Add</b> or drag and drop</h3>
                    <p>Max file size 2 MB</p>
                    <input accept='image/*' onChange={(e) => onPick(e?.target?.files)} type='file' />
                </label>
                <h4>Or</h4>
                <input id='Image_URL_Input' placeholder='Enter image URL' onChange={(e) => onURL(e?.target?.value)} />
                <div className='Modal_Bottom_Con'>
                    {selectFiles?.map((item) => {
                        return <img src={item?.url} />
                    })}
                    <div className='FlexCon' />
                    <button className='Modal_Btn4' onClick={() => setShow(false)}>Close</button>
                    <button className='Modal_Btn3' onClick={() => onUpload()}>Upload</button>
                </div>
            </div>
        </div>
    )
})

const AllImages = React.memo(({ onImageCreate }) => {
    const [getImage, { data, loading }] = useLazyQuery(GET_IMAGES)

    useEffect(() => {
        if (!data?.getImages) {
            getImage()
        }
    }, [])

    return (
        <>
            <div className='h-5' />
            <div className='Presentation_Left_Image_Search_Con'>
                <form onSubmit={(e) => {
                    e.preventDefault()
                    const val = document.getElementById("image_search").value
                    getImage({
                        variables: {
                            "search": val
                        }
                    })
                }} className='Presentation_Left_Image_Search'>
                    <RiSearch2Line className='Presentation_Left_Image_Search_Icon' />
                    <input id='image_search' placeholder='Search Images' onKeyDown={(e) => e?.stopPropagation()} />
                </form>
                <UploadBtn1 />
            </div>
            {loading ?
                <div className='Presentation_Left_Image_Load_Con'>
                    Loading...
                </div>
                :
                <div className='Presentation_Left_Image_Con'>
                    <div className='Presentation_Left_Image_Con1'>
                        {data?.getImages?.map((item, index) => {
                            if (index % 2 === 0) {
                                return <img onDragStart={(e) => e.preventDefault()} key={index} id={item?.id} alt='' className='Presentation_Left_Image' src={item?.previewURL} onClick={() => onImageCreate(item?.largeImageURL)} />
                            } else {
                                return null
                            }
                        })}
                    </div>
                    <div className='Presentation_Left_Image_Con1'>
                        {data?.getImages?.map((item, index) => {
                            if (index % 2 !== 0) {
                                return <img onDragStart={(e) => e.preventDefault()} key={index} id={item?.id} alt='' className='Presentation_Left_Image' src={item?.previewURL} onClick={() => onImageCreate(item?.largeImageURL)} />
                            } else {
                                return null
                            }
                        })}
                        {/* <img onDragStart={(e) => e.preventDefault()} className='Presentation_Left_Image' src='https://i.pinimg.com/originals/46/41/61/4641611401ecb508c625eebe448da663.gif' onClick={() => onImageCreate('https://i.pinimg.com/originals/46/41/61/4641611401ecb508c625eebe448da663.gif')} /> */}
                    </div>
                </div>
            }
        </>
    )
})

const AllGifs = React.memo(({ onImageCreate }) => {
    const [getGif, { data, loading }] = useLazyQuery(GET_GIFS)

    useEffect(() => {
        if (!data?.getGifs) {
            getGif({
                variables: {
                    "type": "stickers"
                }
            })
        }
    }, [])

    return (
        <>
            <div className='h-5' />
            <div className='Presentation_Left_Image_Search_Con'>
                <form onSubmit={(e) => {
                    e.preventDefault()
                    const val = document.getElementById("gif_search").value
                    getGif({
                        variables: {
                            "search": val,
                            "type": "stickers"
                        }
                    })
                }} className='Presentation_Left_Image_Search'>
                    <RiSearch2Line className='Presentation_Left_Image_Search_Icon' />
                    <input id='gif_search' placeholder='Search GIFs' onKeyDown={(e) => e?.stopPropagation()} />
                </form>
                <UploadBtn1 />
            </div>
            {loading ?
                <div className='Presentation_Left_Image_Load_Con'>
                    Loading...
                </div>
                :
                <div className='Presentation_Left_Image_Con'>
                    <div className='Presentation_Left_Image_Con1'>
                        {data?.getGifs?.map((item, index) => {
                            if (index % 2 === 0) {
                                return <img onDragStart={(e) => e.preventDefault()} key={index} id={item?.id} alt='' className='Presentation_Left_Image' src={item?.medium} onClick={() => onImageCreate(item?.medium)} />
                            } else {
                                return null
                            }
                        })}
                    </div>
                    <div className='Presentation_Left_Image_Con1'>
                        {data?.getGifs?.map((item, index) => {
                            if (index % 2 !== 0) {
                                return <img onDragStart={(e) => e.preventDefault()} key={index} id={item?.id} alt='' className='Presentation_Left_Image' src={item?.medium} onClick={() => onImageCreate(item?.medium)} />
                            } else {
                                return null
                            }
                        })}
                    </div>
                </div>
            }
        </>
    )
})

const KeyboardShortcut = React.memo(() => {
    const params = useParams()

    const selected = useStore((state) => state?.slides[state.select].elements?.find((e) => e?.id === state.selects[0]))
    const deleteElements = useStore((state) => state.deleteElements)
    const addElements = useStore((state) => state.addElements)
    const past = useStore((state) => state.past)
    const future = useStore((state) => state.future)
    const undo = useStore((state) => state.undo)
    const redo = useStore((state) => state.redo)
    const slides = useStore((state) => state.slides)
    const title = useStore((state) => state.title)
    const isFullScreen = useStore((state) => state.isFullScreen)
    const setSelectPrev = useStore((state) => state.setSelectPrev)
    const setSelectNext = useStore((state) => state.setSelectNext)

    const [update] = useMutation(UPDATE_PRESENTATION)

    useEffect(() => {
        const handleKeyDown = (event) => {
            console.log(event?.key);
            if (event.key === 'Delete' || event.key === 'Backspace') {
                deleteElements()
            } else if ((event.ctrlKey || event.metaKey) && event.key === "d" && selected) {
                event?.preventDefault()
                addElements({ ...selected, id: nanoid(7), currPosition: { x: selected?.currPosition?.x + 10, y: selected?.currPosition?.y + 10 }, prevPosition: { x: selected?.currPosition?.x + 10, y: selected?.currPosition?.y + 10 }, })
            } else if ((event.ctrlKey || event.metaKey) && event.key === "s") {
                event?.preventDefault()
                toast.promise(
                    update({
                        variables: {
                            "updatePresentationId": params?.id,
                            "present": {
                                "title": title,
                                "slides": slides?.map((itm) => {
                                    return {
                                        "background": itm?.background,
                                        "bgType": itm?.bgType,
                                        "id": itm?._id || "",
                                        "presentId": params?.id,
                                        "elements": JSON.stringify(itm?.elements)
                                    }
                                })
                            }
                        }
                    }),
                    {
                        loading: "Saving",
                        success: "Saved",
                        error: (err) => `${err?.message}`
                    }
                )
            } else if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === "z") {
                if (future?.length > 0) {
                    event?.preventDefault()
                    redo()
                }
            } else if ((event.ctrlKey || event.metaKey) && event.key === "z") {
                if (past?.length > 0) {
                    event?.preventDefault()
                    undo(past[past?.length - 1])
                }
            } else if ((event.ctrlKey || event.metaKey) && event.key === "y") {
                if (future?.length > 0) {
                    event?.preventDefault()
                    redo()
                }
            } else if (isFullScreen && event.key === 'ArrowRight') {
                setSelectNext()
            } else if (isFullScreen && event.key === 'ArrowLeft') {
                setSelectPrev()
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [selected, slides, isFullScreen]);
})

const FontLoader = React.memo(() => {
    const fonts = useStore((state) => state.fonts)

    const loadFonts = () => {
        fonts?.forEach((itm) => {
            const font = { name: itm?.font?.split(",")[0]?.slice(1, -1), category: itm?.font?.split(",")[1] }
            const allFont = allFonts.find((e) => e?.name === font?.name && e?.category === font?.category)
            const linkId = `font-link-${allFont?.sane}`;
            if (document.getElementById(linkId)) return;
            const link = document.createElement("link");
            link.id = linkId;
            link.rel = "stylesheet";
            link.href = `https://fonts.googleapis.com/css2?family=${font?.name?.replace(/\s+/g, "+")}:ital,wght@${allFont?.variants?.sort()?.join(";")}`;
            document.head.appendChild(link);
        })
    }

    useEffect(() => {
        loadFonts()
    }, [fonts])
})

const Network = React.memo(() => {
    const [offline, setOffline] = useState(false)
    const { isOnline } = useNetworkStatus()

    useEffect(() => {
        if (!isOnline) {
            toast.error("You are offline")
            setOffline(true)
        } else if (offline) {
            toast.success("Back to online")
            setOffline(false)
        }
    }, [isOnline])
})

const Header = React.memo(({ data }) => {
    const params = useParams()
    const [show, setShow] = useState(false)
    const setIsFullScreen = useStore((state) => state.setIsFullScreen)
    const title = useStore((state) => state.title)
    const setTitle = useStore((state) => state.setTitle)
    const past = useStore((state) => state.past)
    const future = useStore((state) => state.future)
    const undo = useStore((state) => state.undo)
    const redo = useStore((state) => state.redo)
    const slides = useStore((state) => state.slides)
    const active = useStore((state) => state.slides[state.select]?._id)

    const [updates] = useMutation(UPDATE_PRESENTATION_STATUS, {
        refetchQueries: [
            {
                query: GET_PRESENTATION_STATUS,
                variables: {
                    getPresentationId: params?.id
                }
            }
        ]
    })
    const [update, { loading }] = useMutation(UPDATE_PRESENTATION_STATUS, {
        refetchQueries: [
            {
                query: GET_PRESENTATION_STATUS,
                variables: {
                    getPresentationId: params?.id
                }
            }
        ]
    })
    const [updateTitle] = useMutation(UPDATE_PRESENTATION, {
        refetchQueries: [
            {
                query: GET_PRESENTATION_TITLE,
                variables: {
                    getPresentationId: params?.id
                }
            }
        ]
    })

    const [update1] = useMutation(UPDATE_PRESENTATION)

    const onUpdate = () => {
        update({
            variables: {
                "updatePresentationStatusId": params?.id,
                "status": data?.status === "Live" ? "" : "Live",
                "code": data?.code,
                "activeSlide": active
            },
        }).then((e) => {
            if (e?.data?.updatePresentationStatus === "Done" && data?.status !== "Live") {
                setTimeout(() => {
                    toggleFullscreen()
                }, 1000);
            }
        })
    }

    const onUpdateSlide = () => {
        updates({
            variables: {
                "updatePresentationStatusId": params?.id,
                "status": "Live",
                "code": data?.code,
                "activeSlide": active
            },
        })
    }

    useEffect(() => {
        if (data?.status === "Live") {
            onUpdateSlide()
        }
    }, [active, data])

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen()
                .then(() => {
                    setIsFullScreen(true)
                })
                .catch((error) => console.error('Error attempting to enable full-screen mode:', error));
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen()
                    .then(() => {
                        setIsFullScreen(false)
                    })
                    .catch((error) => console.error('Error attempting to exit full-screen mode:', error));
            }
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullScreen(document.fullscreenElement !== null);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    return (
        <div className='Presentation_Head'>
            <Link to={'/'} className='Presentation_Logo_Con'>
                <div className='Presentation_Logo' />
            </Link>
            <input value={title || ""} onBlur={(e) => {
                if (title?.trim() === "") {
                    setTitle(data?.title)
                } else if (title?.trim() !== data?.title) {
                    updateTitle({
                        variables: {
                            "updatePresentationId": params?.id,
                            "present": {
                                "title": title?.trim(),
                            }
                        }
                    })
                }
            }} onChange={(e) => setTitle(e?.target?.value)} />
            <div className='FlexCon' />
            <div className='Presentation_Head_Menu_Con'>
                <div className='Presentation_Head_Menu Presentation_Head_Menu_Active'>Editor</div>
                <div className='Presentation_Head_Menu'>Report</div>
                <div className='Presentation_Head_Menu'>Settings</div>
            </div>
            <div className='FlexCon' />
            <div onClick={() => {
                toast.promise(
                    update1({
                        variables: {
                            "updatePresentationId": params?.id,
                            "present": {
                                "title": title,
                                "slides": slides?.map((itm) => {
                                    return {
                                        "background": itm?.background,
                                        "bgType": itm?.bgType,
                                        "id": itm?._id || "",
                                        "presentId": params?.id,
                                        "elements": JSON.stringify(itm?.elements)
                                    }
                                })
                            }
                        }
                    }),
                    {
                        loading: "Saving",
                        success: "Saved",
                        error: (err) => `${err?.message}`
                    }
                )
            }} className={'Presentation_Head_Icon'}>
                <FloppyDisk size={18} />
            </div>
            {/* <div onClick={() => past?.length > 0 ? undo(past[past?.length - 1]) : null} className={past?.length > 0 ? 'Presentation_Head_Icon' : 'Presentation_Head_Icon Presentation_Head_Icon_Disable'}>
                <ArrowUUpLeft size={18} />
            </div>
            <div onClick={() => future?.length > 0 ? redo() : null} className={future?.length > 0 ? 'Presentation_Head_Icon' : 'Presentation_Head_Icon Presentation_Head_Icon_Disable'}>
                <ArrowUUpRight size={18} />
            </div> */}
            <div className='Presentation_Head_Btn_Con'>
                <button onClick={() => data?.status === "Live" ? toggleFullscreen() : onUpdate()} className={data?.status === "Live" ? 'Presentation_Head_Btn1 Presentation_Head_Btn_Red' : 'Presentation_Head_Btn1'}>{data?.status === "Live" ? <div className='Presentation_Head_Btn_Dot' /> : null}{loading ? data?.status === "Live" ? "Stopping" : "Presenting" : data?.status ? data?.status : "Present"}</button>
                <div tabIndex={0} onBlur={(e) => {
                    if (e.currentTarget.contains(e.relatedTarget)) {
                        return
                    }
                    setShow(false)
                }}>
                    <button onClick={() => data?.status === "Live" ? onUpdate() : setShow(!show)} className={data?.status === "Live" ? 'Presentation_Head_Btn2 Presentation_Head_Btn_Red' : 'Presentation_Head_Btn2'}>
                        {data?.status === "Live" ?
                            <Stop weight='fill' />
                            :
                            <CaretDown weight='fill' />
                        }
                    </button>
                    <div className={show ? 'Presentation_Header_Modal Presentation_Header_Modal_Open' : 'Presentation_Header_Modal'}>
                        <div className='Presentation_Header_Modal_Option'>
                            Copy code {data?.code}
                        </div>
                        <div onClick={() => toggleFullscreen()} className='Presentation_Header_Modal_Option'>
                            Full Screen
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
})

const SideBar = React.memo(({ inter }) => {
    const leftSelect = useStore((state) => state.leftSelect)
    const setLeftSelect = useStore((state) => state.setLeftSelect)

    return (
        <div className='Presentation_SideBar'>
            <div onClick={() => setLeftSelect("Slides")} className={leftSelect === 'Slides' ? 'Presentation_SideBar_Icon Presentation_SideBar_Icon_Active' : 'Presentation_SideBar_Icon'}>
                <Cards weight={leftSelect === 'Slides1' ? 'fill' : 'regular'} size={23} />
            </div>
            <div onClick={() => setLeftSelect("Texts")} className={leftSelect === 'Texts' ? 'Presentation_SideBar_Icon Presentation_SideBar_Icon_Active' : 'Presentation_SideBar_Icon'}>
                <TextT weight={leftSelect === 'Texts1' ? 'fill' : 'regular'} size={23} />
            </div>
            <div onClick={() => setLeftSelect("Images")} className={leftSelect === 'Images' ? 'Presentation_SideBar_Icon Presentation_SideBar_Icon_Active' : 'Presentation_SideBar_Icon'}>
                <Images weight={leftSelect === 'Images1' ? 'fill' : 'regular'} size={23} />
            </div>
            <div onClick={() => setLeftSelect("Gif")} className={leftSelect === 'Gif' ? 'Presentation_SideBar_Icon Presentation_SideBar_Icon_Active' : 'Presentation_SideBar_Icon'}>
                <Gif weight={leftSelect === 'Gif1' ? 'fill' : 'regular'} size={23} />
            </div>
            <div onClick={() => setLeftSelect("Shapes")} className={leftSelect === 'Shapes' ? 'Presentation_SideBar_Icon Presentation_SideBar_Icon_Active' : 'Presentation_SideBar_Icon'}>
                <Shapes weight={leftSelect === 'Shapes1' ? 'fill' : 'regular'} size={23} />
            </div>
            <div onClick={() => setLeftSelect("Interactions")} className={leftSelect === 'Interactions' ? 'Presentation_SideBar_Icon Presentation_SideBar_Icon_Active' : 'Presentation_SideBar_Icon'}>
                <HandTap weight={leftSelect === 'Interactions1' ? 'fill' : 'regular'} size={23} />
            </div>
            <div onClick={() => setLeftSelect("Upload")} className={leftSelect === 'Upload' ? 'Presentation_SideBar_Icon Presentation_SideBar_Icon_Active' : 'Presentation_SideBar_Icon'}>
                <UploadSimple weight={leftSelect === 'Upload1' ? 'fill' : 'regular'} size={23} />
            </div>
            {inter ?
                <div onClick={() => setLeftSelect("Edit")} className={leftSelect === 'Edit' ? 'Presentation_SideBar_Icon Presentation_SideBar_Icon_Active' : 'Presentation_SideBar_Icon'}>
                    <PencilSimple weight={leftSelect === 'Edit1' ? 'fill' : 'regular'} size={23} />
                </div>
                : null}
        </div>
    )
})

const Slide = React.memo(({ elements, data }) => {
    const outerDivRef = useRef(null);
    const scale = useStore((state) => state.scale)
    const setScale = useStore((state) => state.setScale)
    const background = useStore((state) => state.slides[state.select].background)
    const bgType = useStore((state) => state.slides[state.select].bgType)
    const select = useStore((state) => state.select)

    const handleResize = () => {
        if (outerDivRef.current) {
            const { offsetWidth, offsetHeight } = outerDivRef.current;
            const scaleFactor = Math.min(offsetWidth / 854, offsetHeight / 480);
            setScale(scaleFactor)
        }
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        handleResize()
    }, [])

    const answers = data?.allSlides?.[select]?.answers

    return (
        <div ref={outerDivRef} id='Presentation_Middle_Canvas_Con' className='Presentation_Middle_Canvas_Con'>
            <div style={{ transform: `scale(${scale})`, backgroundColor: bgType === 'Solid Color' ? background : null, backgroundImage: bgType !== 'Solid Color' ? background : "none" }} className='Presentation_Middle_Canvas'>
                {elements?.map((item, index) => {
                    if (item?.type === 'Text') {
                        return <TextComponent key={index} item={item} isFullScreen={data?.status === "Live"} />
                    } else if (item?.type === 'Image') {
                        return <ImageComponent key={index} item={item} isFullScreen={data?.status === "Live"} />
                    } else if (item?.type === 'SVG') {
                        return <SvgComponent key={index} item={item} isFullScreen={data?.status === "Live"} />
                    } else if (item?.type === 'MCQ') {
                        return <MCQComponent key={index} item={item} isFullScreen={data?.status === "Live"} answers={answers} />
                    } else if (item?.type === 'QR') {
                        return <QRComponent key={index} item={item} isFullScreen={data?.status === "Live"} />
                    } else if (item?.type === 'Group') {
                        return <GroupComponent key={index} item={item} isFullScreen={data?.status === "Live"} />
                    } else if (item?.type === 'Word Cloud') {
                        return <WordComponent key={index} item={item} isFullScreen={data?.status === "Live"} answers={answers} />
                    } else if (item?.type === 'Q&A') {
                        return <QuestComponent key={index} item={item} isFullScreen={data?.status === "Live"} answers={answers} />
                    } else {
                        return null
                    }
                })}
                <Guide />
            </div>
        </div>
    )
})

const Guide = React.memo(() => {
    const dragging = useStore((state) => state.dragging)
    const guide = useStore((state) => state.guide)

    if (dragging) {
        return (
            guide === 'V-Center' ?
                <div className='Presentation_V_Center_Guide' />
                : guide === 'H-Center' ?
                    <div className='Presentation_H_Center_Guide' />
                    : guide === 'Center' ?
                        <>
                            <div className='Presentation_V_Center_Guide' />
                            <div className='Presentation_H_Center_Guide' />
                        </>
                        : guide === 'V-Left' ?
                            <>
                                <div className='Presentation_Left_Guide' />
                                <div className='Presentation_V_Center_Guide' />
                            </>
                            : guide === 'V-Right' ?
                                <>
                                    <div className='Presentation_Right_Guide' />
                                    <div className='Presentation_V_Center_Guide' />
                                </>
                                : guide === 'Left' ?
                                    <div className='Presentation_Left_Guide' />
                                    : guide === 'Right' ?
                                        <div className='Presentation_Right_Guide' />
                                        : guide === 'Top' ?
                                            <div className='Presentation_Top_Guide' />
                                            : guide === 'Bottom' ?
                                                <div className='Presentation_Bottom_Guide' />
                                                : guide === 'Top-Left' ?
                                                    <>
                                                        <div className='Presentation_Left_Guide' />
                                                        <div className='Presentation_Top_Guide' />
                                                    </>
                                                    : guide === 'H-Top' ?
                                                        <>
                                                            <div className='Presentation_Top_Guide' />
                                                            <div className='Presentation_H_Center_Guide' />
                                                        </>
                                                        : guide === 'H-Bottom' ?
                                                            <>
                                                                <div className='Presentation_Bottom_Guide' />
                                                                <div className='Presentation_H_Center_Guide' />
                                                            </>
                                                            : null
        )
    }
})

const Slides = React.memo(() => {
    const outerDivRef = useRef(null);
    const setSelect = useStore((state) => state.setSelect)
    const setPreviewScale = useStore((state) => state.setPreviewScale)
    const slidesLen = useStore((state) => state?.slides?.length)

    const handleResize = () => {
        if (outerDivRef.current) {
            const { offsetWidth, offsetHeight } = outerDivRef.current;
            const scaleFactor = Math.min(offsetWidth / 854, offsetHeight / 480);
            setPreviewScale(scaleFactor)
        }
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        handleResize()
    }, [])

    return (
        new Array(slidesLen).fill('1')?.map((item, index) => {
            return (
                <div key={index} style={{ marginTop: index ? 10 : 15 }} onClick={() => setSelect(index)} className={'Presentation_Left_Slide_Preview_Con'}>
                    <div className='Presentation_Left_Slide_Preview_Num'>{index + 1}</div>
                    <SlidePreview outerDivRef={outerDivRef} index={index} />
                </div>
            )
        })
    )
})

const NewSlideBtn = React.memo(() => {
    const params = useParams()
    const addSlide = useStore((state) => state.addSlide)
    const [create, { loading }] = useMutation(CREATE_SLIDE)

    const onCreate = () => {
        create({
            variables: {
                "slide": {
                    bgType: "Solid Color",
                    background: "#ffffff",
                    presentId: params?.id
                }
            }
        }).then((e) => {
            addSlide(e.data.createSlide)
        })
    }

    return (
        <div className='Presentation_Left_Slide_Preview_Add' onClick={() => loading ? null : onCreate()}>
            {loading ?
                <Spinner className='Presentation_Preview_Add_Load' />
                :
                <Plus weight='bold' />
            }
            New Slide
        </div>
    )
})

const UploadBtn1 = React.memo(() => {
    const setShowModal = useStore((state) => state?.setShowModal)
    return (
        <button onClick={() => setShowModal("Upload")}>
            <RiImageAddLine size={16} />
        </button>
    )
})

const UploadBtn = React.memo(() => {
    const setShowModal = useStore((state) => state?.setShowModal)

    return (
        <div className='Presentation_Left_Slide_Upload' onClick={() => setShowModal("Upload")}>
            <Upload weight='bold' />
            Upload File
        </div>
    )
})

const SlidePreview = React.memo(({ outerDivRef, index }) => {
    const item = useStore((state) => state?.slides[index])
    const select = useStore((state) => state.select)
    const previewScale = useStore((state) => state.previewScale)

    return (
        <div ref={outerDivRef} className={select === index ? 'Presentation_Left_Slide_Preview Presentation_Left_Slide_Preview_Select' : 'Presentation_Left_Slide_Preview'}>
            <div onContextMenu={(e) => e?.preventDefault()} className='Presentation_Left_Slide_Preview1' style={{ transform: `scale(${previewScale})`, backgroundColor: item?.bgType === 'Solid Color' ? item?.background : null, backgroundImage: item?.bgType !== 'Solid Color' ? item?.background : "none" }}>
                <SlidePreviewElements item={item} />
            </div>
        </div >
    )
})

const SlidePreviewElements = React.memo(({ item }) => {
    return item?.elements?.map((itm, ind) => {
        return (
            <div key={ind} style={{ position: "absolute", transform: `translate(${itm?.currPosition?.x}px, ${itm?.currPosition?.y}px)` }}>
                {itm?.type === 'Text' ?
                    <div key={ind} style={{ ...itm?.style }} suppressContentEditableWarning dangerouslySetInnerHTML={{ __html: itm?.value?.replaceAll('\n', '<br/>') }} />
                    : itm?.type === 'Image' ?
                        <img key={ind} alt='' draggable={false} src={itm?.value} style={{ objectFit: "fill", ...itm?.style, }} />
                        : itm?.type === 'SVG' ?
                            <ReplaceSvgColor key={ind} value={itm?.value} colors={itm?.colors} style={itm?.style} />
                            : itm?.type === 'MCQ' || itm?.type === 'Word Cloud' || itm?.type === 'Q&A' ?
                                <div key={ind} style={{ ...itm?.style, overflow: "hidden", alignItems: "center" }}>
                                    <div style={{ fontSize: 20, fontWeight: "500", textAlign: "center" }}>{itm?.title}</div>
                                </div>
                                : itm?.type === 'Group' ?
                                    <SlidePreviewElements key={ind} item={itm} />
                                    :
                                    null}
            </div>
        )
    })
})

const GroupComponent = React.memo(({ item }) => {
    const selects = useStore((state) => state.selects)
    const selected = selects?.find((e) => e === item?.id)
    const editElements = useStore((state) => state.editElements)
    const addSelects = useStore((state) => state.addSelects)
    const setClickedPosition = useStore((state) => state.setClickedPosition)
    const setDragging = useStore((state) => state.setDragging)
    const setResizing = useStore((state) => state.setResizing)

    const onMouseDown = (event) => {
        window.getSelection().removeAllRanges()
        addSelects(item?.id)
        editElements("prevPosition", { x: item?.currPosition?.x, y: item?.currPosition?.y }, item?.id)
        editElements("preHeight", item?.style?.height, item?.id)
        editElements("preWidth", item?.style?.width, item?.id)
        setClickedPosition({ x: event.clientX, y: event.clientY })
        if (!selected) {
            setDragging(true)
        }
    }

    const onMouseHandleDown = (event, cursor, side) => {
        event.stopPropagation()
        window.getSelection().removeAllRanges()
        editElements("prevPosition", { x: item?.currPosition?.x, y: item?.currPosition?.y }, item?.id)
        editElements("preWidth", item?.style?.width, item?.id)
        editElements("preHeight", item?.style?.height, item?.id)
        setResizing({ cursor: cursor, side })
        setClickedPosition({ x: event.clientX, y: event.clientY })
    }

    return (
        <div className={selects[0] === item?.id ? 'Presentation_Group_Select Presentation_Group_Selected' : 'Presentation_Group_Select'} style={{ position: "absolute", transform: `translate(${item?.currPosition?.x}px, ${item?.currPosition?.y}px)`, ...item?.style }} onMouseDown={onMouseDown} onClick={(event) => {
            event.stopPropagation()
            if (!selected) {
                addSelects(item?.id)
            }
        }}>
            {item?.elements?.map((itm, ind) => {
                if (itm?.type === 'Text') {
                    return <TextGroupComponent key={ind} item={itm} group={item} />
                } else if (itm?.type === 'Image') {
                    return <ImageGroupComponent key={ind} item={itm} group={item} />
                } else if (itm?.type === 'SVG') {
                    return <SvgGroupComponent key={ind} item={itm} group={item} />
                } else {
                    return null
                }
            })}
            {/* {selects[0] === item?.id ? <>
                <div className='Presentation_Image_Handle Presentation_Image_Handle_LT' onMouseDown={(e) => onMouseHandleDown(e, 'nwse-resize', "top-left")} />
                <div className='Presentation_Image_Handle Presentation_Image_Handle_LB' onMouseDown={(e) => onMouseHandleDown(e, 'nesw-resize', "bottom-left")} />
                <div className='Presentation_Image_Handle Presentation_Image_Handle_RT' onMouseDown={(e) => onMouseHandleDown(e, 'nesw-resize', "top-right")} />
                <div className='Presentation_Image_Handle Presentation_Image_Handle_RB' onMouseDown={(e) => onMouseHandleDown(e, 'nwse-resize', "bottom-right")} />
            </> : null} */}
        </div>
    )
})

const TextGroupComponent = React.memo(({ item, group, relative }) => {
    const resizing = useStore((state) => state.resizing)
    const dragging = useStore((state) => state.dragging)
    const setDragging = useStore((state) => state.setDragging)
    const editing = useStore((state) => state.editing)
    const setEditing = useStore((state) => state.setEditing)
    const editElements = useStore((state) => state.editElements)
    const setResizing = useStore((state) => state.setResizing)
    const addSelect = useStore((state) => state.addSelect)
    const selects = useStore((state) => state.selects)

    const selected = selects?.find((e) => e === item?.id)

    const inputRef = useRef(null);

    const selectAllText = () => {
        if (inputRef.current !== document.activeElement) {
            const el = inputRef.current;
            if (!el) return;

            const range = document.createRange();
            const selection = window.getSelection();

            range.selectNodeContents(el);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    };

    const onBlur = (event) => {
        const val = event?.currentTarget?.innerText
        const elements = group?.elements?.map((itm) => {
            if (itm?.id === item?.id) {
                return {
                    ...itm,
                    value: val
                }
            } else {
                return itm
            }
        })
        editElements("elements", elements, group?.id)
        setEditing(false)
    }

    const onMouseDown = (event) => {
        window.getSelection().removeAllRanges()
        if (inputRef.current !== document.activeElement) {
            event.preventDefault()
        }
        addSelect([group?.id, item?.id])
        editElements("prevPosition", { x: group?.currPosition?.x, y: group?.currPosition?.y }, group?.id)
        if (!selected) {
            setDragging(true)
        }
    }

    const onMouseUp = () => {
        if (resizing) {
            setResizing(false)
        } else if (!dragging) {
            setEditing(true)
        }
    }

    return (
        <div className={selected ? 'Presentation_Text_Select Presentation_Text_Selected' : 'Presentation_Text_Select'} style={{ position: relative || "absolute", transform: `translate(${item?.currPosition?.x}px, ${item?.currPosition?.y}px)`, cursor: resizing ? "ew-resize" : dragging ? "move" : selected ? "text" : "default", userSelect: "none" }} onMouseUp={onMouseUp} onMouseDown={onMouseDown} onClick={(event) => {
            event.stopPropagation()
            if (!selected) {
                addSelect([group?.id, item?.id])
            }
            if (editing) {
                selectAllText()
            }
        }}>
            <div ref={inputRef} className='Presentation_Text' contentEditable={!dragging} spellCheck={false} onBlur={onBlur} onKeyDown={(e) => e?.stopPropagation()} style={{ ...item?.style }} suppressContentEditableWarning dangerouslySetInnerHTML={{ __html: item?.value?.replaceAll('\n', '<br/>') }} />
        </div>
    )
})

const ImageGroupComponent = React.memo(({ item, group }) => {
    const selects = useStore((state) => state.selects)
    const dragging = useStore((state) => state.dragging)
    const selected = selects?.find((e) => e === item?.id)
    const resizing = useStore((state) => state.resizing)
    const addSelect = useStore((state) => state.addSelect)
    const setDragging = useStore((state) => state.setDragging)
    const editElements = useStore((state) => state.editElements)

    const onMouseDown = (event) => {
        addSelect([group?.id, item?.id])
        editElements("prevPosition", { x: group?.currPosition?.x, y: group?.currPosition?.y }, group?.id)
        if (!selected) {
            setDragging(true)
        }
    }

    return (
        <div className={selected ? 'Presentation_Image_Select Presentation_Image_Selected' : 'Presentation_Image_Select'} style={{ position: "absolute", transform: `translate(${item?.currPosition?.x}px, ${item?.currPosition?.y}px)`, cursor: resizing ? "ew-resize" : dragging ? "move" : selected ? "text" : "default", userSelect: "none" }} onMouseDown={onMouseDown} onClick={(event) => {
            event.stopPropagation()
            if (!selected) {
                addSelect([group?.id, item?.id])
            }
        }}>
            <img alt='' draggable={false} src={item?.value} style={{ ...item?.style }} />
        </div>
    )
})

const SvgGroupComponent = React.memo(({ item, group }) => {
    const selects = useStore((state) => state.selects)
    const dragging = useStore((state) => state.dragging)
    const selected = selects?.find((e) => e === item?.id)
    const resizing = useStore((state) => state.resizing)
    const addSelect = useStore((state) => state.addSelect)
    const setDragging = useStore((state) => state.setDragging)
    const editElements = useStore((state) => state.editElements)

    const onMouseDown = (event) => {
        addSelect([group?.id, item?.id])
        editElements("prevPosition", { x: group?.currPosition?.x, y: group?.currPosition?.y }, group?.id)
        if (!selected) {
            setDragging(true)
        }
    }

    return (
        <div className={selected ? 'Presentation_Image_Select Presentation_Image_Selected' : 'Presentation_Image_Select'} style={{ position: "absolute", transform: `translate(${item?.currPosition?.x}px, ${item?.currPosition?.y}px)`, cursor: resizing ? "ew-resize" : dragging ? "move" : selected ? "text" : "default", userSelect: "none" }} onMouseDown={onMouseDown} onClick={(event) => {
            event.stopPropagation()
            if (!selected) {
                addSelect([group?.id, item?.id])
            }
        }}>
            <ReplaceSvgColor value={item?.value} colors={item?.colors} style={item?.style} />
        </div>
    )
})

const ReplaceSvgColor = React.memo(({ value, colors, style }) => {
    let values = value || ''
    colors?.forEach(colorMap => {
        const [originalColor, newColor] = Object.entries(colorMap)[0];
        const regex = new RegExp(originalColor, 'g');
        values = values?.replace(regex, newColor);
    })
    return <div style={{ display: "flex" }} dangerouslySetInnerHTML={{ __html: values?.replace('<svg ', `<svg width="${style?.width}" height="${style?.height}" opacity="${style?.opacity || 1}"`) }} />
})

const TextOptions = React.memo(() => {
    const editElements = useStore((state) => state.editElements)
    const id = useStore((state) => state?.slides[state?.select]?.elements?.find((e) => e?.id === state.selects[0])?.id)
    const style = useStore((state) => state?.slides[state?.select]?.elements?.find((e) => e?.id === state.selects[0])?.style)
    const bullet = useStore((state) => state?.slides[state?.select]?.elements?.find((e) => e?.id === state.selects[0])?.bullet)
    const fonts = useStore((state) => state.fonts)
    const setFonts = useStore((state) => state.setFonts)

    const onChange = (name, val) => {
        const styles = { ...style, [name]: val }
        editElements("style", styles, id)
    }

    const onChangeFont = (name, val) => {
        const styles = { ...style, [name]: val }
        editElements("style", styles, id)
        if (fonts?.find((e) => e?.font === val)) {
            setFonts(fonts?.map((itm) => {
                if (itm?.font === val) {
                    return { font: val, count: itm?.count + 1 }
                } else {
                    return itm
                }
            }))
        } else {
            setFonts([...fonts, { font: val, count: 1 }])
        }
    }

    const textAlign = (align) => {
        if (!align || align === 'left') {
            return "center"
        } else if (align === 'center') {
            return "right"
        } else if (align === 'right') {
            return "justify"
        } else if (align === 'justify') {
            return "left"
        }
    }

    return (
        <>
            <div className='Presentation_Middle_Top_Sep' />
            <FontPick value={style?.fontFamily} setValue={(e) => onChangeFont("fontFamily", e)} />
            <FontSize value={style?.fontSize} setValue={(e) => onChange("fontSize", e)} />
            <div className='Presentation_Middle_Top_Sep_Line' />
            <div className={style?.fontWeight >= 700 ? 'Presentation_Middle_Top_Btn Presentation_Middle_Top_Btn_Active' : 'Presentation_Middle_Top_Btn'} onClick={() => style?.fontWeight >= 700 ? onChange("fontWeight", "500") : onChange("fontWeight", "700")}>
                <TextB size={17} weight='bold' />
            </div>
            <div className={style?.fontStyle === 'italic' ? 'Presentation_Middle_Top_Btn Presentation_Middle_Top_Btn_Active' : 'Presentation_Middle_Top_Btn'} onClick={() => style?.fontStyle === 'italic' ? onChange("fontStyle", "normal") : onChange("fontStyle", "italic")}>
                <TextItalic size={17} />
            </div>
            <div className={style?.textDecoration === 'underline' ? 'Presentation_Middle_Top_Btn Presentation_Middle_Top_Btn_Active' : 'Presentation_Middle_Top_Btn'} onClick={() => style?.textDecoration === 'underline' ? onChange("textDecoration", "none") : onChange("textDecoration", "underline")}>
                <TextUnderline size={17} />
            </div>
            <div className={style?.textDecoration === 'line-through' ? 'Presentation_Middle_Top_Btn Presentation_Middle_Top_Btn_Active' : 'Presentation_Middle_Top_Btn'} onClick={() => style?.textDecoration === 'line-through' ? onChange("textDecoration", "none") : onChange("textDecoration", "line-through")}>
                <TextStrikethrough size={17} />
            </div>
            <ColorPick value={style?.color || "#000000"} onChange={(e) => onChange("color", e)} element={<div className='Presentation_Middle_Top_Btn'><TextT size={17} /><div className='Presentation_Middle_Top_Color_Ind' style={{ backgroundColor: style?.color || "#000000" }} /></div>} />
            <ColorPick value={style?.backgroundColor || "transparent"} onChange={(e) => onChange("backgroundColor", e)} element={<div className='Presentation_Middle_Top_Btn'><Highlighter size={17} /><div className='Presentation_Middle_Top_Color_Ind' style={{ backgroundColor: style?.backgroundColor || "transparent" }} /></div>} />
            <div className='Presentation_Middle_Top_Sep_Line' />
            <div className='Presentation_Middle_Top_Btn' onClick={() => onChange("textAlign", textAlign(style?.textAlign))}>
                {textAligns?.find((e) => e?.name === style?.textAlign)?.icon}
            </div>
            <Paragraphs line={style?.lineHeight} letter={style?.letterSpacing} onChange={onChange} element={<div className='Presentation_Middle_Top_Btn'>
                <Paragraph size={17} />
            </div>} />
            <div className='Presentation_Middle_Top_Sep_Line' />
            <div className={bullet ? 'Presentation_Middle_Top_Btn Presentation_Middle_Top_Btn_Active' : 'Presentation_Middle_Top_Btn'} onClick={() => bullet ? editElements("bullet", false, id) : editElements("bullet", true, id)}>
                <ListBullets size={17} />
            </div>
        </>
    )
})

const DeleteOptions = React.memo(() => {
    const params = useParams()
    const selects = useStore((state) => state.selects.length)
    const deleteElements = useStore((state) => state.deleteElements)
    const deleteSlide = useStore((state) => state.deleteSlide)
    const select = useStore((state) => state.select)
    const setSelect = useStore((state) => state.setSelect)
    const selected = useStore((state) => state?.slides[state.select])

    const [deletes, { loading }] = useMutation(DELETE_SLIDE, {
        refetchQueries: [
            {
                query: GET_ONE_PRESENTATION,
                variables: {
                    getPresentationId: params?.id
                }
            }
        ]
    })

    const onDelete = (index) => {
        deletes({
            variables: {
                "deleteSlideId": selected?._id
            }
        }).then(() => {
            setSelect(select > 0 ? select - 1 : 0)
            deleteSlide(index)
        })
    }

    if (selects > 0) {
        return (
            <div className='Presentation_Middle_Top_DelBtn' onClick={deleteElements}>
                <Trash color='red' size={17} />
            </div>
        )
    } else {
        return (
            <div style={{ color: "red" }} onClick={() => {
                onDelete(select)
            }} className='Presentation_Middle_Top_Btn2'>{loading ? "Deleting" : "Delete Slide"}</div>
        )
    }
})

const LayerOptions = React.memo(() => {
    const elements = useStore((state) => state?.slides[state.select].elements)
    const editSlide = useStore((state) => state?.editSlide)
    const select = useStore((state) => state.selects[0])
    const addSelects = useStore((state) => state.addSelects)

    return (
        <Layer select={select} datas={elements} addSelects={addSelects} editSlide={editSlide} element={<div className='Presentation_Middle_Top_Btn'><RxLayers size={17} /></div>} closeElement={<div className='Presentation_Middle_Top_Btn'><RxCross1 size={17} /></div>} />
    )
})

const GroupOption = React.memo(() => {
    const selects = useStore((state) => state.selects)
    const groupSelect = useStore((state) => state?.slides[state.select].elements?.find((e) => e?.id === state.selects[0]))
    const selected = useStore((state) => state?.slides[state.select].elements?.find((e) => e?.id === state.selects[0])?.elements?.find((e) => e?.id === state.selects[1]))
    const editElements = useStore((state) => state.editElements)
    const addElements = useStore((state) => state.addElements)
    const deleteElements = useStore((state) => state.deleteElements)

    const onChange = (name, val) => {
        const styles = { ...selected?.style, [name]: val }
        const elements = groupSelect?.elements?.map((itm, ind) => {
            if (itm?.id === selects[1]) {
                return {
                    ...itm,
                    style: styles
                }
            } else {
                return itm
            }
        })
        editElements("elements", elements, selects[0])
    }

    const textAlign = (align) => {
        if (!align || align === 'left') {
            return "center"
        } else if (align === 'center') {
            return "right"
        } else if (align === 'right') {
            return "justify"
        } else if (align === 'justify') {
            return "left"
        }
    }

    const onUngroup = () => {
        deleteElements()
        groupSelect?.elements?.forEach((item) => {
            addElements({
                ...item,
                prevPosition: {
                    x: item?.currPosition?.x + groupSelect?.currPosition?.x,
                    y: item?.currPosition?.y + groupSelect?.currPosition?.y
                },
                currPosition: {
                    x: item?.currPosition?.x + groupSelect?.currPosition?.x,
                    y: item?.currPosition?.y + groupSelect?.currPosition?.y
                }
            })
        })
    }

    if (selected?.type === 'Text') {
        return (
            <>
                <div className='Presentation_Middle_Top_Sep' />
                <FontPick value={selected?.style?.fontFamily} setValue={(e) => onChange("fontFamily", e)} />
                <FontSize value={selected?.style?.fontSize} setValue={(e) => onChange("fontSize", e)} />
                <div className='Presentation_Middle_Top_Sep_Line' />
                <div className={selected?.style?.fontWeight >= 700 ? 'Presentation_Middle_Top_Btn Presentation_Middle_Top_Btn_Active' : 'Presentation_Middle_Top_Btn'} onClick={() => selected?.style?.fontWeight >= 700 ? onChange("fontWeight", "500") : onChange("fontWeight", "700")}>
                    <TextB size={17} weight='bold' />
                </div>
                <div className='Presentation_Middle_Top_Btn'>
                    <TextItalic size={17} />
                </div>
                <div className={selected?.style?.textDecoration === 'underline' ? 'Presentation_Middle_Top_Btn Presentation_Middle_Top_Btn_Active' : 'Presentation_Middle_Top_Btn'} onClick={() => selected?.style?.textDecoration === 'underline' ? onChange("textDecoration", "none") : onChange("textDecoration", "underline")}>
                    <TextUnderline size={17} />
                </div>
                <ColorPick value={selected?.style?.color || "#000000"} onChange={(e) => onChange("color", e)} element={<div className='Presentation_Middle_Top_Btn'><TextT size={17} /><div className='Presentation_Middle_Top_Color_Ind' style={{ backgroundColor: selected?.style?.color || "#000000" }} /></div>} />
                <div className='Presentation_Middle_Top_Sep_Line' />
                <div className='Presentation_Middle_Top_Btn' onClick={() => onChange("textAlign", textAlign(selected?.style?.textAlign))}>
                    {textAligns?.find((e) => e?.name === selected?.style?.textAlign)?.icon}
                </div>
                <div className='Presentation_Middle_Top_Btn'>
                    <RxLineHeight size={17} />
                </div>
            </>
        )
    }
    return (
        <>
            <div className='Presentation_Middle_Top_Sep' />
            <div className='Presentation_Middle_Top_Btn1' onClick={() => onUngroup()}>Ungroup</div>
            <div className='Presentation_Middle_Top_Btn1' onClick={() => console.log(groupSelect)}>Log</div>
        </>
    )
})

const CanvasOptions = React.memo(() => {
    const bgType = useStore((state) => state.slides[state.select].bgType)
    const background = useStore((state) => state.slides[state.select].background)
    const editSlide = useStore((state) => state.editSlide)

    const generateImg = async (id) => {
        try {
            const elem = document.getElementById(id)
            await htmlToImage.toPng(elem, { quality: 1, skipFonts: false })
                .then((dataUrl) => {
                    const link = document.createElement('a');
                    link.href = dataUrl;
                    link.download = 'my-image.png';
                    link.click();
                })
        } catch (error) {
            console.log(error);
        }
    }

    const onBgTypeChange = (e) => {
        editSlide("bgType", e)
        if (e === 'Solid Color') {
            editSlide("background", "#ffffff")
        } else if (e === 'Image') {
            editSlide("background", "url(https://images.unsplash.com/photo-1503455637927-730bce8583c0?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8cHB0JTIwYmFja2dyb3VuZHxlbnwwfHwwfHx8MA%3D%3D)")
        } else if (e === 'Gradient') {
            editSlide("background", "linear-gradient(180deg, rgb(0, 0, 0) 0.00%,rgba(255, 255, 255, 1) 100.00%)")
        }
    }

    return (
        <>
            <div className='Presentation_Middle_Top_Sep' />
            <Select value={bgType} setValue={(e) => onBgTypeChange(e)} data={['Solid Color', 'Gradient', 'Image']} width={150} />
            {bgType === 'Image' ?
                <div className='Presentation_Middle_Top_Btn1'>Change Image</div>
                :
                <ColorPick value={background} onChange={(e) => editSlide("background", e)} gradient={bgType === 'Gradient'} element={<div style={{ background: background }} className='Presentation_Middle_Top_Color_Btn' />} />
            }
            {/* <div className='Presentation_Middle_Top_Sep_Line' />
            <div className='Presentation_Middle_Top_Btn'>
                <PaintBrushBroad size={17} />
            </div> */}
            {/* <div onClick={()=>generateImg("Presentation_Middle_Canvas_Con")} className='Presentation_Middle_Top_Btn'>
                <DownloadSimple size={17} />
            </div> */}
        </>
    )
})

const SvgOptions = React.memo(() => {
    const editElements = useStore((state) => state.editElements)
    const id = useStore((state) => state?.slides[state?.select]?.elements?.find((e) => e?.id === state.selects[0])?.id)
    const colors = useStore((state) => state?.slides[state?.select]?.elements?.find((e) => e?.id === state.selects[0])?.colors)
    const style = useStore((state) => state?.slides[state?.select]?.elements?.find((e) => e?.id === state.selects[0])?.style)

    const onChangeColor = (index, value) => {
        const array = [...colors]
        const key = Object.keys(array[index])[0];
        array[index][key] = value;
        editElements("colors", array, id)
    }

    const onChange = (name, val) => {
        const styles = { ...style, [name]: val }
        editElements("style", styles, id)
    }

    return (
        <>
            <div className='Presentation_Middle_Top_Sep' />
            {/* <div className='Presentation_Middle_Top_Btn'>
                <Crop size={17} />
            </div> */}
            {colors?.map((item, index) => {
                return <ColorPick key={index} value={Object.values(item)[0]} onChange={(e) => onChangeColor(index, e)} element={<div style={{ backgroundColor: Object.values(item)[0] }} className='Presentation_Middle_Top_Color_Btn' />} />
            })}
            <div className='Presentation_Middle_Top_Sep_Line' />
            <h3>Opacity</h3>
            <input className='Presentation_Middle_Top_Input' type='number' value={style?.opacity || 1} step={0.1} min={0} max={1} onChange={(e) => onChange("opacity", e?.target?.value * 1)} />
        </>
    )
})

const ImageOptions = React.memo(() => {
    const editElements = useStore((state) => state.editElements)
    const id = useStore((state) => state?.slides[state?.select]?.elements?.find((e) => e?.id === state.selects[0])?.id)
    const style = useStore((state) => state?.slides[state?.select]?.elements?.find((e) => e?.id === state.selects[0])?.style)
    const selected = useStore((state) => state?.slides[state.select].elements?.find((e) => e?.id === state.selects[0]))

    const onChange = (name, val) => {
        const styles = { ...style, [name]: val }
        editElements("style", styles, id)
    }

    return (
        <>
            <div className='Presentation_Middle_Top_Sep' />
            <Select data={["Fill", "Cover", "Contain"]} width={120} value={style?.objectFit || "Fill"} setValue={(e) => onChange("objectFit", e)} />
            <div className='Presentation_Middle_Top_Btn'>
                <Crop size={17} />
            </div>
            <div className='Presentation_Middle_Top_Sep_Line' />
            {/* <div className='Presentation_Middle_Top_Btn'>
                <Funnel size={17} />
            </div> */}
            <Border borderRadius={style?.borderRadius} onBorderRadiusChange={(e) => onChange("borderRadius", e)} element={
                <div className='Presentation_Middle_Top_Btn'>
                    <TbBorderStyle size={17} />
                </div>
            } />
            <Position element={
                <div className='Presentation_Middle_Top_Btn'>
                    <RiDragMoveLine size={17} />
                </div>
            } x={selected?.currPosition?.x} y={selected?.currPosition?.y} onChangePosition={(x, y) => editElements("currPosition", { x, y }, id)} />
            <div className='Presentation_Middle_Top_Sep_Line' />
            <h3>Opacity</h3>
            <input className='Presentation_Middle_Top_Input' type='number' value={style?.opacity || 1} step={0.1} min={0} max={1} onChange={(e) => onChange("opacity", e?.target?.value * 1)} />
        </>
    )
})

const MCQOptions = React.memo(() => {
    const editElements = useStore((state) => state.editElements)
    const id = useStore((state) => state?.slides[state?.select]?.elements?.find((e) => e?.id === state.selects[0])?.id)
    const style = useStore((state) => state?.slides[state?.select]?.elements?.find((e) => e?.id === state.selects[0])?.style)

    const setLeftSelect = useStore((state) => state.setLeftSelect)
    const leftSelect = useStore((state) => state.leftSelect)

    const onChange = (name, val) => {
        const styles = { ...style, [name]: val }
        editElements("style", styles, id)
    }

    const selects = useStore((state) => state.selects)
    const groupSelect = useStore((state) => state?.slides[state.select].elements?.find((e) => e?.id === state.selects[0]))
    const selected = useStore((state) => state?.slides[state.select].elements?.find((e) => e?.id === state.selects[0])?.elements?.find((e) => e?.id === state.selects[1]))

    const onChangeElement = (name, val) => {
        const styles = { ...selected?.style, [name]: val }
        const elements = groupSelect?.elements?.map((itm, ind) => {
            if (itm?.id === selects[1]) {
                return {
                    ...itm,
                    style: styles
                }
            } else {
                return itm
            }
        })
        editElements("elements", elements, selects[0])
    }

    const textAlign = (align) => {
        if (!align || align === 'left') {
            return "center"
        } else if (align === 'center') {
            return "right"
        } else if (align === 'right') {
            return "justify"
        } else if (align === 'justify') {
            return "left"
        }
    }

    if (selected?.type === 'Text') {
        return (
            <>
                <div className='Presentation_Middle_Top_Sep' />
                <FontPick value={selected?.style?.fontFamily} setValue={(e) => onChangeElement("fontFamily", e)} />
                <FontSize value={selected?.style?.fontSize} setValue={(e) => onChangeElement("fontSize", e)} />
                <div className='Presentation_Middle_Top_Sep_Line' />
                <div className={selected?.style?.fontWeight >= 700 ? 'Presentation_Middle_Top_Btn Presentation_Middle_Top_Btn_Active' : 'Presentation_Middle_Top_Btn'} onClick={() => selected?.style?.fontWeight >= 700 ? onChangeElement("fontWeight", "500") : onChangeElement("fontWeight", "700")}>
                    <TextB size={17} weight='bold' />
                </div>
                <div className='Presentation_Middle_Top_Btn'>
                    <TextItalic size={17} />
                </div>
                <div className={selected?.style?.textDecoration === 'underline' ? 'Presentation_Middle_Top_Btn Presentation_Middle_Top_Btn_Active' : 'Presentation_Middle_Top_Btn'} onClick={() => selected?.style?.textDecoration === 'underline' ? onChangeElement("textDecoration", "none") : onChangeElement("textDecoration", "underline")}>
                    <TextUnderline size={17} />
                </div>
                <ColorPick value={selected?.style?.color || "#000000"} onChange={(e) => onChangeElement("color", e)} element={<div className='Presentation_Middle_Top_Btn'><TextT size={17} /><div className='Presentation_Middle_Top_Color_Ind' style={{ backgroundColor: selected?.style?.color || "#000000" }} /></div>} />
                <div className='Presentation_Middle_Top_Sep_Line' />
                <div className='Presentation_Middle_Top_Btn' onClick={() => onChangeElement("textAlign", textAlign(selected?.style?.textAlign))}>
                    {textAligns?.find((e) => e?.name === selected?.style?.textAlign)?.icon}
                </div>
                <div className='Presentation_Middle_Top_Btn'>
                    <RxLineHeight size={17} />
                </div>
            </>
        )
    }
    return (
        <>
            <div className='Presentation_Middle_Top_Sep' />
            {/* <FontPick value={style?.fontFamily} setValue={(e) => onChange("fontFamily", e)} /> */}
            {/* <ColorPick value={headStyle?.color || "#000000"} onChange={(e) => onChangeText("color", e)} element={<div className='Presentation_Middle_Top_Btn'><TextT size={17} /><div className='Presentation_Middle_Top_Color_Ind' style={{ backgroundColor: headStyle?.color || "#000000" }} /></div>} /> */}
            {/* <div className='Presentation_Middle_Top_Sep_Line' /> */}
            <ColorPick value={style?.backgroundColor} onChange={(e) => onChange("backgroundColor", e)} element={<div style={{ background: style?.backgroundColor }} className='Presentation_Middle_Top_Color_Btn' />} />
            {/* <ColorPick value={headStyle?.backgroundColor} onChange={(e) => onChangeHead("backgroundColor", e)} element={<div style={{ background: headStyle?.backgroundColor }} className='Presentation_Middle_Top_Color_Btn' />} /> */}
            {/* <div className='Presentation_Middle_Top_Btn'>
                <TbBorderStyle size={17} />
            </div> */}
            <Border borderRadius={style?.borderRadius} onBorderRadiusChange={(e) => onChange("borderRadius", e)} element={
                <div className='Presentation_Middle_Top_Btn'>
                    <TbBorderStyle size={17} />
                </div>
            } />
            {/* <div className='Presentation_Middle_Top_Btn1'>Show Response</div> */}
            {leftSelect === 'Edit' ?
                null
                :
                <>
                    <div className='Presentation_Middle_Top_Sep_Line' />
                    <div className='Presentation_Middle_Top_Btn1' onClick={() => setLeftSelect("Edit")}>Edit</div>
                </>
            }
        </>
    )
})

function generateRandomArray(count) {
    let randomNumbers = Array.from({ length: count }, () => Math.random());
    let sum = randomNumbers.reduce((acc, num) => acc + num, 0);
    return randomNumbers.map(num => Math.round((num / sum) * 100));
}

function checkIfDuplicateExists(arr) {
    return new Set(arr).size !== arr.length
}

const QAEdit = React.memo(() => {
    const selected = useStore((state) => state?.slides[state.select].elements?.find((e) => e?.id === state.selects[0]))
    const editElements = useStore((state) => state.editElements)

    return (
        <div className='Presentation_Left_Edit_Con'>
            <p>Question</p>
            <input className='Presentation_Left_Edit_Input' value={selected?.title || ''} onKeyDown={(e) => e?.stopPropagation()} onChange={(e) => editElements("title", e?.target?.value, selected?.id)} />
        </div>
    )
})

const WordEdit = React.memo(() => {
    const selected = useStore((state) => state?.slides[state.select].elements?.find((e) => e?.id === state.selects[0]))
    const editElements = useStore((state) => state.editElements)

    return (
        <div className='Presentation_Left_Edit_Con'>
            <p>Question</p>
            <input className='Presentation_Left_Edit_Input' value={selected?.title || ''} onKeyDown={(e) => e?.stopPropagation()} onChange={(e) => editElements("title", e?.target?.value, selected?.id)} />
            {/* <p>Colors</p> */}
        </div>
    )
})

const MCQEdit = React.memo(() => {
    const selected = useStore((state) => state?.slides[state.select].elements?.find((e) => e?.id === state.selects[0]))
    const editElements = useStore((state) => state.editElements)

    const onOptionChange = (ind, val) => {
        const options = [...selected?.options]
        const newOpt = options?.map((e, i) => {
            if (i == ind) {
                return val
            } else {
                return e
            }
        })
        editElements("options", newOpt, selected?.id)
    }

    const onAddOption = () => {
        const options = [...selected?.options, `Option ${selected?.options?.length + 1}`]
        editElements("options", options, selected?.id)
        editElements("sampleValues", generateRandomArray(options?.length), selected?.id)
    }

    const onRemoveOption = (opt) => {
        const options = selected?.options?.filter((e) => e !== opt)
        editElements("options", options, selected?.id)
        editElements("sampleValues", generateRandomArray(options?.length), selected?.id)
    }

    const duplicate = checkIfDuplicateExists(selected?.options)

    return (
        <div className='Presentation_Left_Edit_Con'>
            <p>Question</p>
            <input className='Presentation_Left_Edit_Input' value={selected?.title || ''} onKeyDown={(e) => e?.stopPropagation()} onChange={(e) => editElements("title", e?.target?.value, selected?.id)} />
            <p>Options</p>
            <RearrangeOptions selected={selected} setItems={(e) => editElements("options", e, selected?.id)} onOptionChange={onOptionChange} onRemoveOption={onRemoveOption} />
            {duplicate ? <p style={{ color: "red" }}>Please ensure all options are unique</p> : null}
            <button style={{ cursor: selected?.options?.length < 7 ? "pointer" : "not-allowed", opacity: selected?.options?.length < 7 ? 1 : 0.7 }} onClick={() => selected?.options?.length < 6 ? onAddOption() : null}>Add Option +</button>
            {/* <p>Button</p>
            <input className='Presentation_Left_Edit_Input' value={selected?.elements[2]?.value || ""} onChange={(e) => editElements()} /> */}
            {selected?.max ?
                <>
                    <p>Max Selection</p>
                    <input className='Presentation_Left_Edit_Input' type='number' value={selected?.max} min={1} max={selected?.options?.length} onChange={(e) => editElements("max", e?.target?.value, selected?.id)} />
                </>
                : null}
        </div>
    )
})

const RearrangeOptions = React.memo(({ selected, setItems, onOptionChange, onRemoveOption }) => {
    const outerDiv = useRef(null);
    const [dragId, setDragId] = useState(null)
    const [clickedPosition, setClickedPosition] = useState({ x: -1, y: -1 })
    const [click, setClick] = useState({ x: 0, y: 0 })
    const [delta, setDelta] = useState({ x: 0, y: 0 })

    const onMouseDown = (e, id) => {
        e.stopPropagation()
        setClickedPosition({ x: e.clientX, y: e.clientY })
        const outerRect = outerDiv?.current?.getBoundingClientRect();
        const clickX = e.clientX - outerRect?.left;
        const clickY = e.clientY - outerRect?.top;
        setClick({ x: clickX - 16, y: clickY - 30 })
        setDelta({ x: clickX - 16, y: clickY - 30 })
        if (clickX) {
            setDragId(id)
        }
    }

    const onMouseUp = (e) => {
        e.stopPropagation()
        if (dragId) {
            setDragId(null)
            setClickedPosition({ x: -1, y: -1 })
            setDelta({ x: 0, y: 0 })
        }
    }

    const onMouseMove = useCallback((event) => {
        if (clickedPosition.x >= 0 && clickedPosition.y >= 0) {
            const deltaX = (event.clientX - clickedPosition?.x)
            const deltaY = (event.clientY - clickedPosition?.y)
            setDelta({
                x: click.x + deltaX,
                y: click.y + deltaY
            })
        }
    }, [clickedPosition])

    const moveItem = (toIndex) => {
        const fromIndex = selected?.options?.findIndex((e) => e === dragId)
        if (fromIndex < 0 || fromIndex >= selected?.options.length || toIndex < 0 || toIndex >= selected?.options.length) {
            console.error('Invalid indices');
            return;
        }
        const newArray = [...selected?.options];
        const [movedItem] = newArray.splice(fromIndex, 1);
        newArray.splice(toIndex, 0, movedItem);
        setItems(newArray);
    };

    const onMouseEnter = (index) => {
        if (dragId) {
            moveItem(index)
        }
    }

    return (
        <div ref={outerDiv} className='Presentation_Left_Edit_Outer_Option_Con' onMouseMove={(e) => onMouseMove(e)} onMouseUp={onMouseUp} onMouseLeave={onMouseUp} style={{ userSelect: dragId ? "none" : null }}>
            {selected?.options?.map((itm, ind) => {
                if (itm === dragId) {
                    return (
                        <div key={ind} style={{ opacity: 0 }} className='Presentation_Left_Edit_Option_Con'>
                            <div className='Presentation_Left_Edit_Option_Input'>
                                <DotsSixVertical className='Presentation_Left_Edit_Option_Icon1' />
                                <div className='Presentation_Left_Edit_Option_Text'>{dragId}</div>
                            </div>
                            <Trash color='red' className='Presentation_Left_Edit_Option_Icon' />
                        </div>
                    )
                }
                return (
                    <div key={ind} onMouseEnter={() => onMouseEnter(ind)} style={{ zIndex: 50 }} className='Presentation_Left_Edit_Option_Con'>
                        <div className='Presentation_Left_Edit_Option_Input'>
                            <DotsSixVertical onMouseDown={(e) => onMouseDown(e, itm)} className='Presentation_Left_Edit_Option_Icon1' />
                            <input value={itm || ''} onKeyDown={(e) => e?.stopPropagation()} onChange={(e) => onOptionChange(ind, e?.target?.value)} />
                        </div>
                        <Trash onClick={() => selected?.options?.length > 2 ? onRemoveOption(itm) : null} style={{ cursor: selected?.options?.length > 2 ? "pointer" : "not-allowed" }} color='red' className='Presentation_Left_Edit_Option_Icon' />
                    </div>
                )
            })}
            {dragId ?
                <div style={{ top: `${delta.y}px`, left: `${delta.x}px`, position: "absolute", pointerEvents: "none", zIndex: 100 }} className={'Presentation_Left_Edit_Option_Con'}>
                    <div className='Presentation_Left_Edit_Option_Input'>
                        <DotsSixVertical className='Presentation_Left_Edit_Option_Icon1' />
                        <div className='Presentation_Left_Edit_Option_Text'>{dragId}</div>
                    </div>
                    <Trash color='red' className='Presentation_Left_Edit_Option_Icon' />
                </div>
                : null
            }
        </div>
    )
})

const QREdit = React.memo(() => {
    const selected = useStore((state) => state?.slides[state.select].elements?.find((e) => e?.id === state.selects[0]))
    const editElements = useStore((state) => state.editElements)

    const onOptionChange = (ind, val) => {
        const options = [...selected?.options]
        const newOpt = options?.map((e, i) => {
            if (i == ind) {
                return val
            } else {
                return e
            }
        })
        editElements("options", newOpt, selected?.id)
    }

    const contact = 'MECARD:N:John Doe;TEL:+1234567890;EMAIL:johndoe@example.com;URL:https://johndoe.com;ADR:123 Main St, Anytown, State, 12345;ORG:Example Corp;;'

    return (
        <div className='Presentation_Left_Edit_Con'>
            <p>QR Type</p>
            <select value={selected?.qrType} onChange={(e) => editElements("qrType", e?.target?.value, selected?.id)}>
                <option>URL</option>
                <option>Plain Text</option>
                <option>Contact</option>
            </select>
            {selected?.qrType === 'Contact' ?
                <>
                    <p>Name</p>
                    <input onKeyDown={(e) => e?.stopPropagation()} className='Presentation_Left_Edit_Input' />
                    <p>Phone Number</p>
                    <input onKeyDown={(e) => e?.stopPropagation()} className='Presentation_Left_Edit_Input' />
                    <p>Email</p>
                    <input onKeyDown={(e) => e?.stopPropagation()} className='Presentation_Left_Edit_Input' />
                    <p>Website</p>
                    <input onKeyDown={(e) => e?.stopPropagation()} className='Presentation_Left_Edit_Input' />
                    <p>Address</p>
                    <textarea onKeyDown={(e) => e?.stopPropagation()} />
                </>
                :
                selected?.qrType === 'Plain Text' ?
                    <>
                        <p>Enter Value</p>
                        <textarea value={selected?.value} onKeyDown={(e) => e?.stopPropagation()} onChange={(e) => editElements("value", e?.target?.value, selected?.id)} />
                    </>
                    :
                    <>
                        <p>Enter Value</p>
                        <input className='Presentation_Left_Edit_Input' value={selected?.value || ''} onKeyDown={(e) => e?.stopPropagation()} onChange={(e) => editElements("value", e?.target?.value, selected?.id)} />
                    </>
            }
        </div>
    )
})

const texts = [
    { text: "Heading", style: { fontSize: 20, fontWeight: "700", fontFamily: '"Poppins",sans-serif' } },
    { text: "Sub Heading", style: { fontSize: 18, fontWeight: "500", fontFamily: '"Poppins",sans-serif' } },
    { text: "Paragraph", style: { fontSize: 16, fontWeight: "400", fontFamily: '"Poppins",sans-serif' } },
]
//'"Inter",sans-serif'
const styledText = [
    { text: "Retro", style: { fontSize: 22, fontFamily: '"Poppins",sans-serif', textShadow: "0 1px #808d93, -1px 0 #cdd2d5, -1px 2px #808d93, -2px 1px #cdd2d5, -2px 3px #808d93, -3px 2px #cdd2d5, -3px 4px #808d93, -4px 3px #cdd2d5, -4px 5px #808d93, -5px 4px #cdd2d5, -5px 6px #808d93, -6px 5px #cdd2d5, -6px 7px #808d93, -7px 6px #cdd2d5, -7px 8px #808d93, -8px 7px #cdd2d5" } },
    { text: "Shadow", style: { fontSize: 22, fontFamily: '"Poppins",sans-serif', textShadow: "1px 0px 1px #ccc, 0px 1px 1px #eee, 2px 1px 1px #ccc, 1px 2px 1px #eee, 3px 2px 1px #ccc, 2px 3px 1px #eee, 4px 3px 1px #ccc, 3px 4px 1px #eee, 5px 4px 1px #ccc, 4px 5px 1px #eee, 6px 5px 1px #ccc, 5px 6px 1px #eee, 7px 6px 1px #ccc" } },
    { text: "Outline", style: { fontSize: 22, color: "#fff", WebkitTextStroke: "0.5px #000", fontFamily: '"Poppins",sans-serif' } },
    {
        image: text1, element: {
            "type": "Group",
            "style": {
                "width": 200,
                "height": 83.98810754912097
            },
            "elements": [
                {
                    "type": "Text",
                    "value": "Birthday",
                    "style": {
                        "fontSize": 50,
                        "fontWeight": "500",
                        "fontFamily": '"Great Vibes",handwriting',
                        "width": 200,
                        "textAlign": "center",
                        "color": "rgb(252, 185, 0)"
                    },
                    "preWidth": 200,
                    "currPosition": {
                        "x": 0,
                        "y": 20.98810754912097
                    },
                    "prevPosition": {
                        "x": 0,
                        "y": 20.98810754912097
                    },
                    "preHeight": 63
                },
                {
                    "type": "Text",
                    "value": "Happy",
                    "style": {
                        "fontSize": 20,
                        "fontWeight": "500",
                        "fontFamily": '"Poppins",sans-serif',
                        "width": 117.86763185108585,
                        "textAlign": "center",
                        "color": "rgb(0, 0, 0)"
                    },
                    "preWidth": 117.86763185108585,
                    "currPosition": {
                        "x": 41.066184074457055,
                        "y": 0
                    },
                    "prevPosition": {
                        "x": 41.066184074457055,
                        "y": 0
                    },
                    "preHeight": 25
                }
            ],
            "preHeight": 83.98810754912097,
            "preWidth": 200
        }
    }
]

const textAligns = [
    { name: undefined, icon: <TextAlignLeft size={17} /> }, { name: "left", icon: <TextAlignLeft size={17} /> }, { name: "center", icon: <TextAlignCenter size={17} /> }, { name: "right", icon: <TextAlignRight size={17} /> }, { name: "justify", icon: <TextAlignJustify size={17} /> }
]